import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { parseServerDate } from '../lib/date.js';
import { Icon } from '../components/Icon';
import { IconButton } from '../components/ui/IconButton.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { DataTable, type ColumnDef } from '../components/ui/DataTable.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { Pill } from '../components/PillGroup.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { Radio, RadioGroup } from '../components/ui/Radio.js';
import { Tabs } from '../components/ui/Tabs.js';
import { Toggle } from '../components/ui/Toggle.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { Badge } from '../components/ui/Badge.js';
import { SectionTitle } from '../components/ui/SectionTitle.js';
import { Field } from '../components/ui/Field.js';
import { Textarea } from '../components/ui/Textarea.js';
import { FileUploadZone } from '../components/ui/FileUploadZone.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { useToast } from '../components/ui/Toast.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import {
  useAllSettings,
  useUpdateSetting,
  useSecretStatus,
  useUpdateSecret,
  useLlmSpend,
  useEmailAccounts,
  useAddEmailAccount,
  useTestEmailAccount,
  useAccountSignature,
  useUpdateAccountSignature,
  useAuditLog,
  useLeadEngineSettings,
  useUpdateLeadEngineSettings,
  useLeadPipelines,
  useCreateLeadPipeline,
  useUpdateLeadPipeline,
  useDeleteLeadPipeline,
  useRunPipelineNow,
  useOpenClawStatus,
  useIssueOpenClawToken,
  type LeadPipeline,
  type LeadPipelineInput,
  type ScrapeZone,
} from '../hooks/useSettings.js';
import { useCompany, useUpdateCompany, useAccounts, useCreateAccount, useUpdateAccount, useDeleteAccount } from '../hooks/useBilling.js';
import { PipelineEditorModal, SCRAPE_CATEGORY_OPTIONS } from '../components/leads/PipelineEditorModal.js';
import {
  useRules,
  useCreateRule,
  useUpdateRule,
  useDeleteRule,
  type Rule,
  type CreateRuleInput,
} from '../hooks/useRules.js';
import {
  useJunkContacts, useDeleteJunkContacts,
  useRelationshipCategories, useCreateRelationshipCategory, useDeleteRelationshipCategory, useUpdateRelationshipCategory,
  useContactTagsList, useCreateContactTag, useDeleteContactTag,
  type JunkContactItem,
} from '../hooks/useContacts.js';
import { useFollowupRules, useDeleteFollowupRule, type FollowupRule } from '../hooks/useEmail.js';
import { useToolApprovals, useRevokeToolApproval } from '../hooks/useAssistant.js';
import { useUsers, useCreateUser, useUpdateUserRole, useSetUserActive, useApiUsage, useSetApiUsageLimit, useSubscription, useSetSubscriptionPrice, useCreateSubscriptionCheckout, useCreateBillingPortal, type SubscriptionSummary } from '../hooks/useSettings.js';
import { useSession } from '../contexts/SessionContext.js';
import {
  useEmailCategoryDefs, useCreateEmailCategoryDef, useUpdateEmailCategoryDef, useDeleteEmailCategoryDef,
} from '../hooks/useEmailCategories.js';

// ── Section: General ──────────────────────────────────────────────────────────

function GeneralSection() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const updateSetting = useUpdateSetting();
  const { data: settings } = useAllSettings();
  const [calendlyVal, setCalendlyVal] = useState('');
  const [calendlySaved, setCalendlySaved] = useState(false);

  const logoPath = settings?.['brand_logo_path'];
  const iconPath = settings?.['brand_icon_path'];

  const currentCalendly = settings?.['calendly_url'] ?? '';
  useEffect(() => { setCalendlyVal(currentCalendly); }, [currentCalendly]);

  function handleLangChange(lang: string) {
    void i18n.changeLanguage(lang);
    updateSetting.mutate({ key: 'ui_language', value: lang });
  }

  function saveCalendly() {
    updateSetting.mutate(
      { key: 'calendly_url', value: calendlyVal.trim() },
      { onSuccess: () => { setCalendlySaved(true); setTimeout(() => setCalendlySaved(false), 2000); } },
    );
  }

  return (
    <div>
      <SectionTitle>{t('settings.tabs.general')}</SectionTitle>
      <Field label={t('settings.generalSection.languageLabel')} hint={t('settings.generalSection.languageHint')}>
        <Dropdown<string>
          value={i18n.language.startsWith('en') ? 'en' : 'es'}
          options={[
            { value: 'es', label: t('settings.generalSection.langEs') },
            { value: 'en', label: t('settings.generalSection.langEn') },
          ]}
          onChange={handleLangChange}
          size="sm"
        />
      </Field>
      <Field label={t('settings.generalSection.calendlyUrl')} hint={t('settings.generalSection.calendlyUrlHint')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <Input
            type="url"
            value={calendlyVal}
            onChange={v => { setCalendlyVal(v); setCalendlySaved(false); }}
            placeholder="https://calendly.com/tu-usuario/reunion"
            size="sm"
            fullWidth
          />
          <Button
            size="sm"
            tone="cyan"
            variant={calendlySaved ? 'outline' : 'solid'}
            loading={updateSetting.isPending}
            onClick={saveCalendly}
          >
            {updateSetting.isPending ? t('settings.savingBtn') : calendlySaved ? t('settings.savedBtn') : t('settings.saveBtn')}
          </Button>
        </div>
      </Field>

      <Field label={t('settings.generalSection.brandLogo')} hint={t('settings.generalSection.brandLogoHint')}>
        <FileUploadZone
          uploadUrl="/api/settings/brand-asset/logo"
          value={logoPath ? [logoPath] : []}
          onUploaded={() => void qc.invalidateQueries({ queryKey: ['settings'] })}
          onDelete={() => updateSetting.mutate({ key: 'brand_logo_path', value: '' })}
          acceptMultiple={false}
          acceptedTypes="image/png,image/jpeg,image/webp,image/svg+xml"
        />
      </Field>

      <Field label={t('settings.generalSection.brandIcon')} hint={t('settings.generalSection.brandIconHint')}>
        <FileUploadZone
          uploadUrl="/api/settings/brand-asset/icon"
          value={iconPath ? [iconPath] : []}
          onUploaded={() => void qc.invalidateQueries({ queryKey: ['settings'] })}
          onDelete={() => updateSetting.mutate({ key: 'brand_icon_path', value: '' })}
          acceptMultiple={false}
          acceptedTypes="image/png,image/jpeg,image/webp,image/svg+xml"
        />
      </Field>
    </div>
  );
}

// ── Section: API & IA ────────────────────────────────────────────────────────

function ApiSection() {
  const { t } = useTranslation();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();
  const { data: apiKeyStatus } = useSecretStatus('anthropic-api-key');
  const { data: groqKeyStatus } = useSecretStatus('groq-api-key');
  const { data: googleTtsKeyStatus } = useSecretStatus('google-tts-api-key');
  const { data: elevenLabsKeyStatus } = useSecretStatus('elevenlabs-api-key');
  const updateSecret = useUpdateSecret();
  const { data: spend } = useLlmSpend();

  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [apiKeySaved, setApiKeySaved] = useState(false);

  const [groqKey, setGroqKey] = useState('');
  const [showGroqKey, setShowGroqKey] = useState(false);
  const [groqKeySaved, setGroqKeySaved] = useState(false);

  const [googleTtsKey, setGoogleTtsKey] = useState('');
  const [showGoogleTtsKey, setShowGoogleTtsKey] = useState(false);
  const [googleTtsKeySaved, setGoogleTtsKeySaved] = useState(false);

  const [elevenLabsKey, setElevenLabsKey] = useState('');
  const [showElevenLabsKey, setShowElevenLabsKey] = useState(false);
  const [elevenLabsKeySaved, setElevenLabsKeySaved] = useState(false);

  const [voiceIdValue, setVoiceIdValue] = useState('');
  const [voiceIdSaved, setVoiceIdSaved] = useState(false);
  const currentVoiceId = settings?.['elevenlabs-voice-id'] ?? '';

  const [capValue, setCapValue] = useState('');
  const [capSaved, setCapSaved] = useState(false);
  const currentCap = settings?.['daily-cap-usd'] ?? '5.00';

  function saveApiKey() {
    if (!apiKey.trim()) return;
    updateSecret.mutate(
      { key: 'anthropic-api-key', value: apiKey.trim() },
      {
        onSuccess: () => {
          setApiKey('');
          setApiKeySaved(true);
          setTimeout(() => setApiKeySaved(false), 2000);
        },
      },
    );
  }

  function saveGroqKey() {
    if (!groqKey.trim()) return;
    updateSecret.mutate(
      { key: 'groq-api-key', value: groqKey.trim() },
      {
        onSuccess: () => {
          setGroqKey('');
          setGroqKeySaved(true);
          setTimeout(() => setGroqKeySaved(false), 2000);
        },
      },
    );
  }

  function saveGoogleTtsKey() {
    if (!googleTtsKey.trim()) return;
    updateSecret.mutate(
      { key: 'google-tts-api-key', value: googleTtsKey.trim() },
      {
        onSuccess: () => {
          setGoogleTtsKey('');
          setGoogleTtsKeySaved(true);
          setTimeout(() => setGoogleTtsKeySaved(false), 2000);
        },
      },
    );
  }

  function saveElevenLabsKey() {
    if (!elevenLabsKey.trim()) return;
    updateSecret.mutate(
      { key: 'elevenlabs-api-key', value: elevenLabsKey.trim() },
      {
        onSuccess: () => {
          setElevenLabsKey('');
          setElevenLabsKeySaved(true);
          setTimeout(() => setElevenLabsKeySaved(false), 2000);
        },
      },
    );
  }

  function saveVoiceId() {
    const val = voiceIdValue.trim() || currentVoiceId;
    if (!val) return;
    updateSetting.mutate(
      { key: 'elevenlabs-voice-id', value: val },
      { onSuccess: () => { setVoiceIdSaved(true); setTimeout(() => setVoiceIdSaved(false), 2000); } },
    );
  }

  function saveCap() {
    const val = parseFloat(capValue || currentCap);
    if (isNaN(val) || val < 0) return;
    updateSetting.mutate(
      { key: 'daily-cap-usd', value: val.toFixed(2) },
      { onSuccess: () => { setCapSaved(true); setTimeout(() => setCapSaved(false), 2000); } },
    );
  }

  const spendPct = spend ? Math.min(100, (spend.todaySpend / spend.cap) * 100) : 0;
  const spendColor = spendPct >= 90 ? '#ef4444' : spendPct >= 70 ? '#f59e0b' : '#00fac8';

  return (
    <div>
      <SectionTitle>{t('settings.ia.title')}</SectionTitle>

      {/* Anthropic API key */}
      <Field
        label={t('settings.ia.apiKeyLabel')}
        hint={apiKeyStatus?.configured ? t('settings.ia.apiKeyConfigured') : t('settings.ia.apiKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={v => { setApiKey(v); setApiKeySaved(false); }}
            placeholder={apiKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.apiKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowKey(s => !s)}>
            {showKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={apiKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveApiKey}>{updateSecret.isPending ? t('settings.savingBtn') : apiKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Groq API key — transcripción de voz (Fase 4) */}
      <Field
        label={t('settings.ia.groqKeyLabel')}
        hint={groqKeyStatus?.configured ? t('settings.ia.groqKeyConfigured') : t('settings.ia.groqKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showGroqKey ? 'text' : 'password'}
            value={groqKey}
            onChange={v => { setGroqKey(v); setGroqKeySaved(false); }}
            placeholder={groqKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.groqKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowGroqKey(s => !s)}>
            {showGroqKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={groqKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveGroqKey}>{updateSecret.isPending ? t('settings.savingBtn') : groqKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Google Cloud TTS key — voz de salida de Evo Brain (Fase 4.1) */}
      <Field
        label={t('settings.ia.googleTtsKeyLabel')}
        hint={googleTtsKeyStatus?.configured ? t('settings.ia.googleTtsKeyConfigured') : t('settings.ia.googleTtsKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showGoogleTtsKey ? 'text' : 'password'}
            value={googleTtsKey}
            onChange={v => { setGoogleTtsKey(v); setGoogleTtsKeySaved(false); }}
            placeholder={googleTtsKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.googleTtsKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowGoogleTtsKey(s => !s)}>
            {showGoogleTtsKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={googleTtsKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveGoogleTtsKey}>{updateSecret.isPending ? t('settings.savingBtn') : googleTtsKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* ElevenLabs key — voz de salida activa (Fase 4.1) */}
      <Field
        label={t('settings.ia.elevenLabsKeyLabel')}
        hint={elevenLabsKeyStatus?.configured ? t('settings.ia.elevenLabsKeyConfigured') : t('settings.ia.elevenLabsKeyMissing')}
      >
        <div className="flex items-center gap-2">
          <Input
            type={showElevenLabsKey ? 'text' : 'password'}
            value={elevenLabsKey}
            onChange={v => { setElevenLabsKey(v); setElevenLabsKeySaved(false); }}
            placeholder={elevenLabsKeyStatus?.configured ? t('settings.ia.apiKeyMaskPlaceholder') : t('settings.ia.elevenLabsKeyPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button variant="outline" tone="muted" size="sm" onClick={() => setShowElevenLabsKey(s => !s)}>
            {showElevenLabsKey ? t('settings.ia.hideKey') : t('settings.ia.showKey')}
          </Button>
          <Button size="sm" tone="cyan" variant={elevenLabsKeySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveElevenLabsKey}>{updateSecret.isPending ? t('settings.savingBtn') : elevenLabsKeySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* ElevenLabs voice_id — no es secreto, setting DB plano */}
      <Field label={t('settings.ia.elevenLabsVoiceLabel')} hint={t('settings.ia.elevenLabsVoiceHint')}>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            value={voiceIdValue || currentVoiceId}
            onChange={v => { setVoiceIdValue(v); setVoiceIdSaved(false); }}
            placeholder="CFhe2thhS6uqHsZesIpg"
            size="sm"
            fullWidth
          />
          <Button size="sm" tone="cyan" variant={voiceIdSaved ? 'outline' : 'solid'} loading={updateSetting.isPending} onClick={saveVoiceId}>{updateSetting.isPending ? t('settings.savingBtn') : voiceIdSaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Daily cap */}
      <Field label={t('settings.ia.dailyCapLabel')} hint={t('settings.ia.dailyCapHint')}>
        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>$</span>
          <div style={{ width: '96px', flexShrink: 0 }}>
            <Input
              type="number"
              min={0}
              step={0.5}
              value={capValue || currentCap}
              onChange={v => { setCapValue(v); setCapSaved(false); }}
              size="sm"
              fullWidth
            />
          </div>
          <Button size="sm" tone="cyan" variant={capSaved ? 'outline' : 'solid'} loading={updateSetting.isPending} onClick={saveCap}>{updateSetting.isPending ? t('settings.savingBtn') : capSaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
        </div>
      </Field>

      {/* Today's spend */}
      {spend && (
        <Field label={t('settings.ia.spendToday')} hint={`${new Date(spend.date).toLocaleDateString('es', { weekday: 'long', month: 'long', day: 'numeric' })}`}>
          <div className="space-y-1.5">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--evo-bg-2)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${spendPct}%`, background: spendColor }}
                />
              </div>
              <span className="text-sm font-mono w-20 text-right" style={{ color: spendColor }}>
                ${spend.todaySpend.toFixed(4)} / ${spend.cap.toFixed(2)}
              </span>
            </div>
            <p className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
              {t('settings.ia.remaining')} ${spend.remaining.toFixed(4)}
            </p>
          </div>
        </Field>
      )}
    </div>
  );
}

// ── Section: Image Generation (IA) ───────────────────────────────────────────

function ImageGenerationSection() {
  const { t } = useTranslation();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();
  const { data: openaiKeyStatus } = useSecretStatus('openai-api-key');
  const updateSecret = useUpdateSecret();

  const provider = settings?.['image_generation.provider'] ?? 'none';

  const [openaiKey, setOpenaiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [keySaved, setKeySaved] = useState(false);

  function saveOpenaiKey() {
    if (!openaiKey.trim()) return;
    updateSecret.mutate(
      { key: 'openai-api-key', value: openaiKey.trim() },
      {
        onSuccess: () => {
          setOpenaiKey('');
          setKeySaved(true);
          setTimeout(() => setKeySaved(false), 2000);
        },
      },
    );
  }

  return (
    <div style={{ marginTop: 'var(--evo-space-6)', paddingTop: 'var(--evo-space-6)', borderTop: '1px solid var(--evo-stroke)' }}>
      <SectionTitle>{t('settings.imageGen.title', 'Generación de imágenes IA')}</SectionTitle>

      {/* Provider */}
      <Field
        label={t('settings.imageGen.providerLabel', 'Proveedor')}
        hint={t('settings.imageGen.providerHint', 'Servicio de IA para generar imágenes de páginas web.')}
      >
        <RadioGroup
          value={provider}
          options={[
            { value: 'none', label: t('settings.imageGen.providerNone', 'Desactivado') },
            { value: 'openai', label: 'OpenAI' },
          ]}
          onChange={v => updateSetting.mutate({ key: 'image_generation.provider', value: v })}
        />
      </Field>

      {/* OpenAI config — only when openai selected */}
      {provider === 'openai' && (
        <>
          <Field
            label={t('settings.imageGen.openaiKeyLabel', 'OpenAI API Key')}
            hint={openaiKeyStatus?.configured
              ? t('settings.imageGen.openaiKeyConfigured', 'Clave configurada ✓')
              : t('settings.imageGen.openaiKeyMissing', 'Requerida para generar imágenes')}
          >
            <div className="flex items-center gap-2">
              <Input
                type={showKey ? 'text' : 'password'}
                value={openaiKey}
                onChange={v => { setOpenaiKey(v); setKeySaved(false); }}
                placeholder={openaiKeyStatus?.configured ? '••••••••' : 'sk-...'}
                size="sm"
                fullWidth
              />
              <Button variant="outline" tone="muted" size="sm" onClick={() => setShowKey(s => !s)}>
                {showKey ? t('settings.ia.hideKey', 'Ocultar') : t('settings.ia.showKey', 'Mostrar')}
              </Button>
              <Button size="sm" tone="cyan" variant={keySaved ? 'outline' : 'solid'} loading={updateSecret.isPending} onClick={saveOpenaiKey}>{updateSecret.isPending ? t('settings.savingBtn') : keySaved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
            </div>
          </Field>

          <Field
            label={t('settings.imageGen.modelLabel', 'Modelo')}
            hint={t('settings.imageGen.modelHint', 'GPT Image 1 es el único modelo disponible.')}
          >
            <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>
              GPT Image 1
            </span>
          </Field>
        </>
      )}
    </div>
  );
}

// ── Account row with inline signature editor ──────────────────────────────────

function AccountRow({ name }: { name: string }) {
  const { t } = useTranslation();
  const testAccount = useTestEmailAccount();
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);
  const [sigExpanded, setSigExpanded] = useState(false);
  const { data: sigData } = useAccountSignature(name);
  const updateSig = useUpdateAccountSignature();
  const [sigValue, setSigValue] = useState('');
  const [sigSaved, setSigSaved] = useState(false);

  useEffect(() => {
    if (sigData?.signature != null) setSigValue(sigData.signature);
  }, [sigData?.signature]);

  function handleTest() {
    testAccount.mutate(name, {
      onSuccess: r => setTestResult(r),
      onError: (err: Error) => setTestResult({ ok: false, error: err.message }),
    });
  }

  function handleSaveSig() {
    updateSig.mutate({ name, signature: sigValue }, {
      onSuccess: () => { setSigSaved(true); setTimeout(() => setSigSaved(false), 2000); },
    });
  }

  return (
    <div className="rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="text-sm font-medium" style={{ color: 'var(--evo-fg-2)' }}>{name}</span>
        <div className="flex items-center gap-2">
          {testResult && (
            <span className="text-xs" style={{ color: testResult.ok ? '#00fac8' : '#ef4444' }}>
              {testResult.ok
                ? t('settings.imap.connected')
                : <span className="flex items-center gap-1"><Icon name="error" size={13} />{testResult.error ?? 'Error'}</span>
              }
            </span>
          )}
          <Button variant="outline" tone="muted" size="sm" disabled={testAccount.isPending} onClick={handleTest}>
            {testAccount.isPending ? '…' : t('settings.imap.testBtn')}
          </Button>
          <Button
            variant="outline"
            tone={sigExpanded ? 'cyan' : 'muted'}
            size="sm"
            onClick={() => setSigExpanded(s => !s)}
          >
            {t('settings.accounts.signature')}
          </Button>
        </div>
      </div>

      {sigExpanded && (
        <div className="px-3 pb-3 pt-2" style={{ borderTop: '1px solid var(--evo-stroke)' }}>
          <div style={{ marginBottom: '8px' }}>
            <Textarea
              value={sigValue}
              onChange={v => { setSigValue(v); setSigSaved(false); }}
              placeholder={t('settings.accounts.signature_placeholder')}
              rows={3}
              fullWidth
              resize="none"
            />
          </div>
          {sigValue && (
            <div className="text-xs px-2 py-1.5 rounded mb-2 whitespace-pre-wrap"
              style={{ background: 'var(--evo-bg-3)', color: 'var(--evo-fg-3)', border: '1px solid var(--evo-stroke)', fontStyle: 'italic' }}>
              {'-- \n'}{sigValue}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              loading={updateSig.isPending}
              onClick={handleSaveSig}
            >
              {updateSig.isPending ? t('settings.savingBtn') : t('settings.accounts.signature_save')}
            </Button>
            {sigSaved && <span className="text-xs" style={{ color: 'var(--evo-cyan)' }}>{t('settings.accounts.signature_saved')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: IMAP Accounts ────────────────────────────────────────────────────

const POLL_OPTIONS = [
  { value: '1' },
  { value: '5' },
  { value: '15' },
  { value: '30' },
  { value: '60' },
  { value: '0' },
];

const TRASH_OPTIONS = [
  { value: '7' },
  { value: '15' },
  { value: '30' },
  { value: '-1' },
];

// export: reusado tal cual (sin duplicar) por OnboardingPage.tsx — paso
// "conectar email" de la inducción (2026-08-05).
export function ImapSection() {
  const { t } = useTranslation();
  const { data: accounts = [], isLoading } = useEmailAccounts();
  const addAccount = useAddEmailAccount();
  const { data: settings } = useAllSettings();
  const updateSetting = useUpdateSetting();

  const currentInterval = settings?.['imap-poll-interval'] ?? '5';

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', host: '', port: '993', user: '', pass: '' });

  const formFields = [
    { key: 'name', label: t('settings.imap.fields.name'), placeholder: t('settings.imap.placeholders.name'), type: 'text' },
    { key: 'host', label: t('settings.imap.fields.host'), placeholder: t('settings.imap.placeholders.host'), type: 'text' },
    { key: 'port', label: t('settings.imap.fields.port'), placeholder: t('settings.imap.placeholders.port'), type: 'number' },
    { key: 'user', label: t('settings.imap.fields.user'), placeholder: t('settings.imap.placeholders.user'), type: 'text' },
    { key: 'pass', label: t('settings.imap.fields.pass'), placeholder: t('settings.imap.placeholders.pass'), type: 'password' },
  ];

  function setField(key: string, val: string) {
    setForm(f => ({ ...f, [key]: val }));
  }

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    addAccount.mutate(
      { name: form.name, host: form.host, port: form.port, user: form.user, pass: form.pass },
      {
        onSuccess: () => {
          setForm({ name: '', host: '', port: '993', user: '', pass: '' });
          setShowForm(false);
        },
      },
    );
  }

  return (
    <div>
      <SectionTitle>{t('settings.imap.title')}</SectionTitle>

      <Field label={t('settings.imap.pollIntervalLabel')} hint={t('settings.imap.pollIntervalHint')}>
        <Dropdown<string>
          value={currentInterval}
          options={POLL_OPTIONS.map(o => ({ value: o.value, label: t(`settings.imap.poll.${o.value}`) }))}
          onChange={val => updateSetting.mutate({ key: 'imap-poll-interval', value: val })}
          size="sm"
        />
      </Field>

      <Field label={t('settings.imap.trashRetentionLabel')} hint={t('settings.imap.trashRetentionHint')}>
        <Dropdown<string>
          value={settings?.['trash-retention-days'] ?? '30'}
          options={TRASH_OPTIONS.map(o => ({ value: o.value, label: t(`settings.imap.trashOptions.${o.value}`) }))}
          onChange={val => updateSetting.mutate({ key: 'trash-retention-days', value: val })}
          size="sm"
        />
      </Field>

      {isLoading && (
        <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>
      )}

      {accounts.length === 0 && !isLoading && (
        <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>
          {t('settings.imap.noAccounts')}
        </p>
      )}

      <div className="space-y-2 mb-4">
        {accounts.map(name => (
          <AccountRow key={name} name={name} />
        ))}
      </div>

      {showForm ? (
        <form onSubmit={handleAdd} className="space-y-3 p-4 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <p className="text-xs font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.imap.formTitle')}</p>
          {formFields.map(f => (
            <div key={f.key} className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {f.label}
              </label>
              <Input
                type={f.type as 'text' | 'number' | 'password'}
                value={form[f.key as keyof typeof form]}
                onChange={v => setField(f.key, v)}
                placeholder={f.placeholder}
                required={f.key !== 'port'}
                size="sm"
                fullWidth
              />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={addAccount.isPending}>
              {addAccount.isPending ? t('settings.imap.savingAccount') : t('settings.imap.addAccountBtn')}
            </Button>
            <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => setShowForm(false)}>
              {t('settings.imap.cancelBtn')}
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowForm(true)}>
          {t('settings.imap.addAccount')}
        </Button>
      )}
    </div>
  );
}

// ── Section: Rules ────────────────────────────────────────────────────────────

const EMPTY_FORM: CreateRuleInput = {
  matchType: 'sender',
  sender: '',
  domain: '',
  subjectPattern: '',
  originalCategory: '',
  newCategory: '',
};

function RulesSection() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [form, setForm] = useState<CreateRuleInput>(EMPTY_FORM);

  const { data, isLoading } = useRules(page, search);
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  function matchLabel(mt: string): string {
    const k = mt === 'sender+subject' ? 'senderSubject' : mt === 'domain+subject' ? 'domainSubject' : mt;
    return t(`settings.rules.matchTypes.${k}`);
  }

  function setField<K extends keyof CreateRuleInput>(key: K, val: CreateRuleInput[K]) {
    setForm(f => ({ ...f, [key]: val }));
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  }

  function openNew() {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(rule: Rule) {
    setEditingRule(rule);
    setForm({
      matchType: rule.matchType,
      sender: rule.sender,
      domain: rule.domain,
      ...(rule.subjectPattern !== null ? { subjectPattern: rule.subjectPattern } : {}),
      ...(rule.originalCategory !== null ? { originalCategory: rule.originalCategory } : {}),
      newCategory: rule.newCategory,
    });
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingRule(null);
    setForm(EMPTY_FORM);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.newCategory.trim()) return;

    const payload: CreateRuleInput = {
      matchType: form.matchType,
      newCategory: form.newCategory,
      ...(form.sender ? { sender: form.sender } : {}),
      ...(form.domain ? { domain: form.domain } : {}),
      ...(form.subjectPattern ? { subjectPattern: form.subjectPattern } : {}),
      ...(form.originalCategory ? { originalCategory: form.originalCategory } : {}),
    };

    if (editingRule) {
      updateRule.mutate(
        { id: editingRule.id, ...payload },
        { onSuccess: () => handleCancel() },
      );
    } else {
      createRule.mutate(payload, { onSuccess: () => handleCancel() });
    }
  }

  async function handleDelete(rule: Rule) {
    const ok = await confirmAction({
      title: t('settings.rules.deleteConfirm', { pattern: rule.sender || rule.domain }),
      tone: 'danger',
    });
    if (!ok) return;
    deleteRule.mutate(rule.id);
  }

  const isPending = createRule.isPending || updateRule.isPending;

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.rules.title')}</SectionTitle>

      {/* Search + New button */}
      <div className="flex items-center gap-2 mb-4">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1">
          <Input
            type="text"
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('settings.rules.searchPlaceholder')}
            size="sm"
            fullWidth
          />
          <Button type="submit" variant="outline" tone="muted" size="sm">
            {t('settings.rules.searchBtn')}
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              tone="muted"
              size="sm"
              onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}
            >
              <Icon name="close" size={14} />
            </Button>
          )}
        </form>
        {!showForm && (
          <Button variant="solid" tone="cyan" size="sm" onClick={openNew}>
            {t('settings.rules.newRule')}
          </Button>
        )}
      </div>

      {/* Inline form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="space-y-3 p-4 rounded mb-4"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
        >
          <p className="text-xs font-medium" style={{ color: 'var(--evo-fg-3)' }}>
            {editingRule ? t('settings.rules.editTitle') : t('settings.rules.createTitle')}
          </p>

          {/* matchType */}
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('settings.rules.matchTypeLabel')}
            </label>
            <Dropdown<string>
              value={form.matchType}
              options={[
                { value: 'sender', label: t('settings.rules.matchTypes.sender') },
                { value: 'domain', label: t('settings.rules.matchTypes.domain') },
                { value: 'sender+subject', label: t('settings.rules.matchTypes.senderSubject') },
                { value: 'domain+subject', label: t('settings.rules.matchTypes.domainSubject') },
              ]}
              onChange={val => setField('matchType', val as CreateRuleInput['matchType'])}
              size="sm"
            />
          </div>

          {/* sender */}
          {(form.matchType === 'sender' || form.matchType === 'sender+subject') && (
            <div className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {t('settings.rules.fieldSender')}
              </label>
              <Input
                type="text"
                value={form.sender ?? ''}
                onChange={v => setField('sender', v)}
                placeholder={t('settings.rules.placeholderSender')}
                size="sm"
                fullWidth
              />
            </div>
          )}

          {/* domain */}
          {(form.matchType === 'domain' || form.matchType === 'domain+subject') && (
            <div className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {t('settings.rules.fieldDomain')}
              </label>
              <Input
                type="text"
                value={form.domain ?? ''}
                onChange={v => setField('domain', v)}
                placeholder={t('settings.rules.placeholderDomain')}
                size="sm"
                fullWidth
              />
            </div>
          )}

          {/* subjectPattern */}
          {(form.matchType === 'sender+subject' || form.matchType === 'domain+subject') && (
            <div className="flex items-center gap-3">
              <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
                {t('settings.rules.fieldSubjectPattern')}
              </label>
              <Input
                type="text"
                value={form.subjectPattern ?? ''}
                onChange={v => setField('subjectPattern', v)}
                placeholder={t('settings.rules.placeholderSubject')}
                size="sm"
                fullWidth
              />
            </div>
          )}

          {/* originalCategory */}
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('settings.rules.fieldOrigCategory')}
            </label>
            <Input
              type="text"
              value={form.originalCategory ?? ''}
              onChange={v => setField('originalCategory', v)}
              placeholder={t('settings.rules.placeholderOptional')}
              size="sm"
              fullWidth
            />
          </div>

          {/* newCategory */}
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('settings.rules.fieldNewCategory')}
            </label>
            <Input
              type="text"
              value={form.newCategory}
              onChange={v => setField('newCategory', v)}
              placeholder={t('settings.rules.placeholderCategory')}
              required
              size="sm"
              fullWidth
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={isPending}>
              {isPending ? t('settings.rules.savingRule') : editingRule ? t('settings.rules.updateRule') : t('settings.rules.createRule')}
            </Button>
            <Button type="button" variant="outline" tone="muted" size="sm" onClick={handleCancel}>
              {t('settings.rules.cancelBtn')}
            </Button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
              {[
                t('settings.rules.headers.type'),
                t('settings.rules.headers.pattern'),
                t('settings.rules.headers.subject'),
                t('settings.rules.headers.origCat'),
                t('settings.rules.headers.newCat'),
                t('settings.rules.headers.applications'),
                t('settings.rules.headers.actions'),
              ].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</td></tr>
            )}
            {!isLoading && (data?.rows ?? []).length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.rules.noRules')}</td></tr>
            )}
            {(data?.rows ?? []).map(rule => (
              <tr
                key={rule.id}
                onClick={() => openEdit(rule)}
                style={{ borderBottom: '1px solid var(--evo-stroke)', cursor: 'pointer' }}
              >
                <td className="px-3 py-2 font-medium">{matchLabel(rule.matchType)}</td>
                <td className="px-3 py-2 font-mono" style={{ color: 'var(--evo-fg-3)' }}>
                  {rule.sender || rule.domain || '—'}
                </td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{rule.subjectPattern ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{rule.originalCategory ?? '—'}</td>
                <td className="px-3 py-2 font-medium" style={{ color: 'var(--evo-cyan)' }}>{rule.newCategory}</td>
                <td className="px-3 py-2 text-right" style={{ color: 'var(--evo-fg-3)' }}>{rule.appliedCount}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      tone="muted"
                      size="xs"
                      onClick={e => { e.stopPropagation(); openEdit(rule); }}
                    >
                      {t('settings.rules.editBtn')}
                    </Button>
                    <Button
                      variant="outline"
                      tone="danger"
                      size="xs"
                      disabled={deleteRule.isPending}
                      onClick={e => { e.stopPropagation(); void handleDelete(rule); }}
                    >
                      {t('settings.rules.deleteBtn')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs" style={{ color: 'var(--evo-fg-3)' }}>
          <span>{t('settings.rules.paginationText', { count: data.total, page: data.page, pages: data.pages })}</span>
          <div className="flex gap-1">
            <Button variant="ghost" tone="muted" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</Button>
            <Button variant="ghost" tone="muted" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>›</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: Audit Log ────────────────────────────────────────────────────────

function fmtDate(s: string): string {
  return parseServerDate(s).toLocaleString('es', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function AuditSection() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useAuditLog(page, 50);

  return (
    <div>
      <SectionTitle>{t('settings.audit.title')}</SectionTitle>
      <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
              {[
                t('settings.audit.headers.action'),
                t('settings.audit.headers.entity'),
                t('settings.audit.headers.id'),
                t('settings.audit.headers.date'),
              ].map(h => (
                <th key={h} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</td></tr>
            )}
            {isError && (
              <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: '#ef4444' }}>{t('settings.audit.errorLoading')}</td></tr>
            )}
            {!isLoading && !isError && (data?.rows ?? []).length === 0 && (
              <tr><td colSpan={4} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.audit.noRecords')}</td></tr>
            )}
            {(data?.rows ?? []).map(row => (
              <tr
                key={row.id}
                style={{ borderBottom: '1px solid var(--evo-stroke)' }}
              >
                <td className="px-3 py-2 font-medium">{row.action}</td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{row.entityType ?? '—'}</td>
                <td className="px-3 py-2 font-mono" style={{ color: 'var(--evo-fg-3)' }}>{row.entityId ?? '—'}</td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{fmtDate(row.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.pages > 1 && (
        <div className="flex items-center justify-between mt-3 text-xs" style={{ color: 'var(--evo-fg-3)' }}>
          <span>{t('settings.audit.paginationText', { count: data.total, page: data.page, pages: data.pages })}</span>
          <div className="flex gap-1">
            <Button variant="ghost" tone="muted" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</Button>
            <Button variant="ghost" tone="muted" size="sm" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>›</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: Cleanup ──────────────────────────────────────────────────────────

function CleanupSection() {
  const { t } = useTranslation();
  const { data: junk, isLoading } = useJunkContacts();
  const deleteJunk = useDeleteJunkContacts();
  const [showList, setShowList] = useState(false);
  const [selected, setSelected] = useState<JunkContactItem[]>([]);
  const [confirmAll, setConfirmAll] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const [skippedCount, setSkippedCount] = useState(0);

  const total = junk?.total ?? 0;
  const items = junk?.items ?? [];

  function handleDeleteSelected() {
    deleteJunk.mutate(selected.map(c => c.id), {
      onSuccess: res => { setDone(res.deleted); setSkippedCount(res.skipped.length); setSelected([]); },
    });
  }

  function handleDeleteAll() {
    deleteJunk.mutate(undefined, {
      onSuccess: res => { setDone(res.deleted); setSkippedCount(res.skipped.length); setShowList(false); setConfirmAll(false); },
    });
  }

  const columns: ColumnDef<JunkContactItem>[] = [
    {
      id: 'name',
      header: t('settings.cleanup.junkContacts.columnName'),
      accessorFn: row => [row.firstName, row.lastName].filter(Boolean).join(' ') || '—',
    },
    {
      id: 'business',
      header: t('settings.cleanup.junkContacts.columnBusiness'),
      accessorFn: row => row.businessName || '—',
    },
    {
      id: 'email',
      header: t('settings.cleanup.junkContacts.columnEmail'),
      cell: ({ row }) => (
        <span style={{ color: row.original.primaryEmail ? 'var(--evo-fg-2)' : 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)' }}>
          {row.original.primaryEmail || '—'}
        </span>
      ),
    },
    {
      id: 'phone',
      header: t('settings.cleanup.junkContacts.columnPhone'),
      cell: ({ row }) => (
        <span style={{ color: row.original.primaryPhone ? 'var(--evo-fg-2)' : 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)' }}>
          {row.original.primaryPhone || '—'}
        </span>
      ),
    },
    {
      id: 'source',
      header: t('settings.cleanup.junkContacts.columnSource'),
      accessorKey: 'source',
    },
    {
      id: 'reason',
      header: t('settings.cleanup.junkContacts.columnReason'),
      enableSorting: false,
      cell: ({ row }) => (
        <Pill tone="amber" variant="subtle" size="sm">
          {t(row.original.reason === 'no_data'
            ? 'settings.cleanup.junkContacts.reasonNoData'
            : 'settings.cleanup.junkContacts.reasonBothInvalid')}
        </Pill>
      ),
    },
  ];

  return (
    <DetailSection title={t('settings.cleanup.junkContacts.title')}>
      <p style={{ margin: '0 0 var(--evo-space-3)', fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)', lineHeight: 1.5 }}>
        {t('settings.cleanup.junkContacts.description')}
      </p>

      {isLoading ? (
        <LoadingState size="sm" />
      ) : total === 0 && done === null ? (
        <EmptyState title={t('settings.cleanup.junkContacts.empty')} size="sm" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
          {/* Counter + action row */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--evo-space-2)' }}>
            <span style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-semibold)', color: 'var(--evo-amber)' }}>
              {t('settings.cleanup.junkContacts.count', { count: total })}
            </span>

            {total > 0 && (
              <>
                <Button variant="outline" tone="neutral" size="sm" onClick={() => setShowList(l => !l)}>
                  {showList ? t('settings.cleanup.junkContacts.hideList') : t('settings.cleanup.junkContacts.viewList')}
                </Button>

                {!confirmAll && done === null && (
                  <Button variant="outline" tone="danger" size="sm" onClick={() => setConfirmAll(true)}>
                    {t('settings.cleanup.junkContacts.deleteAll')}
                  </Button>
                )}

                {confirmAll && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-danger)' }}>
                      {t('settings.cleanup.junkContacts.confirmDeleteAll', { count: total })}
                    </span>
                    <Button variant="solid" tone="danger" size="sm" loading={deleteJunk.isPending} onClick={handleDeleteAll}>
                      {t('common.delete')}
                    </Button>
                    <Button variant="ghost" tone="muted" size="sm" onClick={() => setConfirmAll(false)}>
                      {t('common.cancel')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {done !== null && (
            <span style={{ fontFamily: 'var(--evo-font)', fontSize: 'var(--evo-fs-label)', color: 'var(--evo-cyan)' }}>
              {t('settings.cleanup.deleted', { count: done })}
              {skippedCount > 0 && (
                <span style={{ color: 'var(--evo-amber)' }}> — {t('settings.cleanup.skipped', { count: skippedCount })}</span>
              )}
            </span>
          )}

          {/* Preview table */}
          {showList && total > 0 && (
            <div>
              <SelectionActionBar
                visible={selected.length > 0}
                countLabel={String(selected.length)}
                onClearSelection={() => setSelected([])}
              >
                <SelectionActionBar.Action
                  label={t('settings.cleanup.junkContacts.deleteSelected', { count: selected.length })}
                  tone="danger"
                  confirmRequired
                  loading={deleteJunk.isPending}
                  onClick={handleDeleteSelected}
                />
              </SelectionActionBar>
              <DataTable<JunkContactItem>
                key={total}
                columns={columns}
                data={items}
                size="sm"
                density="compact"
                selectable
                onSelectionChange={setSelected}
                getRowId={row => String(row.id)}
                emptyMessage={t('settings.cleanup.junkContacts.empty')}
              />
            </div>
          )}
        </div>
      )}
    </DetailSection>
  );
}

// ── Section: Follow-up rules ──────────────────────────────────────────────────

function FollowupRulesSection() {
  const { t } = useTranslation();
  const { data: rules = [], isLoading } = useFollowupRules();
  const deleteRule = useDeleteFollowupRule();
  const [sensitivity, setSensitivity] = useState('moderate');
  const [sensSaved, setSensSaved] = useState(false);
  const [reanalyzeRead, setReanalyzeRead] = useState(false);

  useEffect(() => {
    fetch('/api/settings/key/followup-sensitivity').then(r => r.json()).then((d: { value?: string }) => {
      if (d.value) setSensitivity(d.value);
    }).catch(() => {});
    fetch('/api/settings/key/reanalyze-read-emails').then(r => r.json()).then((d: { value?: string }) => {
      setReanalyzeRead(d.value === 'true');
    }).catch(() => {});
  }, []);

  function saveSensitivity(val: string) {
    setSensitivity(val);
    fetch('/api/settings/key/followup-sensitivity', { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ value: val }) })
      .then(r => { if (!r.ok) throw new Error(); setSensSaved(true); setTimeout(() => setSensSaved(false), 2000); })
      .catch(() => { alert('No se pudo guardar la configuración de sensibilidad.'); });
  }

  function toggleReanalyzeRead(val: boolean) {
    setReanalyzeRead(val);
    fetch('/api/settings/key/reanalyze-read-emails', { method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ value: String(val) }) })
      .then(r => { if (!r.ok) throw new Error(); })
      .catch(() => { alert('No se pudo guardar la configuración de re-análisis.'); });
  }

  return (
    <div>
      <SectionTitle>{t('settings.followup.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.followup.description')}
      </p>
      {/* Sensitivity setting */}
      <div className="mb-6 p-4 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--evo-fg-2)' }}>
          {t('settings.followup.sensitivity.sensitivityLabel')}
        </label>
        <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)' }}>
          {t('settings.followup.sensitivity.sensitivityDesc')}
        </p>
        <div className="flex items-center gap-2">
          <Dropdown<string>
            value={sensitivity}
            options={[
              { value: 'conservative', label: t('settings.followup.sensitivity.conservative') },
              { value: 'moderate', label: t('settings.followup.sensitivity.moderate') },
              { value: 'aggressive', label: t('settings.followup.sensitivity.aggressive') },
            ]}
            onChange={saveSensitivity}
            size="sm"
          />
          {sensSaved && <span className="text-xs" style={{ color: 'var(--evo-cyan)' }}>{t('common.saved')}</span>}
        </div>
      </div>
      {/* Re-analyze read emails toggle */}
      <div className="mb-6 p-4 rounded flex items-start gap-3" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
        <Checkbox
          checked={reanalyzeRead}
          onChange={checked => toggleReanalyzeRead(checked)}
          size="sm"
        />
        <div>
          <label className="text-xs font-medium block mb-0.5" style={{ color: 'var(--evo-fg-2)', cursor: 'pointer' }}>
            {t('settings.followup.reanalyzeReadLabel')}
          </label>
          <p className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
            {t('settings.followup.reanalyzeReadDesc')}
          </p>
        </div>
      </div>
      <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
              {[
                t('settings.followup.headers.type'),
                t('settings.followup.headers.pattern'),
                t('settings.followup.headers.action'),
                t('settings.followup.headers.created'),
                '',
              ].map((h, i) => (
                <th key={i} className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</td></tr>
            )}
            {!isLoading && rules.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.followup.noRules')}</td></tr>
            )}
            {rules.map((rule: FollowupRule) => (
              <tr key={rule.id} style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
                <td className="px-3 py-2 font-medium">{rule.sender ? t('settings.followup.typeSender') : t('settings.followup.typeDomain')}</td>
                <td className="px-3 py-2 font-mono" style={{ color: 'var(--evo-fg-3)' }}>{rule.sender ?? rule.domain ?? '—'}</td>
                <td className="px-3 py-2">
                  <span style={{ color: rule.action === 'always' ? '#f59e0b' : 'var(--evo-fg-3)' }}>
                    {rule.action === 'always' ? t('settings.followup.actionAlways') : t('settings.followup.actionNever')}
                  </span>
                </td>
                <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>
                  {parseServerDate(rule.createdAt).toLocaleDateString('es', { month: 'short', day: 'numeric' })}
                </td>
                <td className="px-3 py-2">
                  <Button variant="ghost" tone="danger" size="sm" disabled={deleteRule.isPending} onClick={() => deleteRule.mutate(rule.id)}>
                    {t('settings.followup.deleteBtn')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Section: Categories & Tags ────────────────────────────────────────────────

function CategoriesTagsSection() {
  const { t } = useTranslation();
  const { data: relCategories = [], isLoading: relLoading } = useRelationshipCategories();
  const createRelCat = useCreateRelationshipCategory();
  const deleteRelCat = useDeleteRelationshipCategory();
  const updateRelCat = useUpdateRelationshipCategory();
  const { data: tags = [], isLoading: tagsLoading } = useContactTagsList();
  const createTag = useCreateContactTag();
  const deleteTag = useDeleteContactTag();

  const { data: emailCategories = [], isLoading: emailCategoriesLoading } = useEmailCategoryDefs();
  const createEmailCat = useCreateEmailCategoryDef();
  const updateEmailCat = useUpdateEmailCategoryDef();
  const deleteEmailCat = useDeleteEmailCategoryDef();
  const [editingEmailCatId, setEditingEmailCatId] = useState<number | null>(null);
  const [editEmailCatName, setEditEmailCatName] = useState('');
  const [editEmailCatColor, setEditEmailCatColor] = useState('#888888');
  const [showNewEmailCat, setShowNewEmailCat] = useState(false);
  const [newEmailCat, setNewEmailCat] = useState({ name: '', color: '#888888' });

  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [editLabelEs, setEditLabelEs] = useState('');
  const [editLabelEn, setEditLabelEn] = useState('');
  const [editColor, setEditColor] = useState('#888888');

  const [showNewCat, setShowNewCat] = useState(false);
  const [newCat, setNewCat] = useState({ slug: '', labelEs: '', color: '#888888' });
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTag, setNewTag] = useState({ slug: '', label: '', color: '#888888' });
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  function handleCreateEmailCat(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmailCat.name.trim()) return;
    createEmailCat.mutate(newEmailCat, {
      onSuccess: () => { setNewEmailCat({ name: '', color: '#888888' }); setShowNewEmailCat(false); },
    });
  }

  async function handleDeleteEmailCat(id: number) {
    const ok = await confirmAction({ title: t('settings.categoriesAndTags.delete_confirm', { count: 0 }), tone: 'danger' });
    if (!ok) return;
    deleteEmailCat.mutate(id);
  }

  function handleCreateCat(e: React.FormEvent) {
    e.preventDefault();
    if (!newCat.slug || !newCat.labelEs) return;
    createRelCat.mutate(
      { slug: newCat.slug, labelEs: newCat.labelEs, labelEn: newCat.labelEs, color: newCat.color },
      { onSuccess: () => { setNewCat({ slug: '', labelEs: '', color: '#888888' }); setShowNewCat(false); } },
    );
  }

  async function handleDeleteCat(slug: string, isSystem: boolean | number, count: number) {
    if (isSystem) { alert(t('settings.categoriesAndTags.cannot_delete_system')); return; }
    const ok = await confirmAction({
      title: t('settings.categoriesAndTags.delete_confirm', { count }),
      tone: 'danger',
    });
    if (!ok) return;
    deleteRelCat.mutate(slug);
  }

  function handleCreateTag(e: React.FormEvent) {
    e.preventDefault();
    if (!newTag.slug || !newTag.label) return;
    createTag.mutate(
      { slug: newTag.slug, label: newTag.label, color: newTag.color },
      { onSuccess: () => { setNewTag({ slug: '', label: '', color: '#888888' }); setShowNewTag(false); } },
    );
  }

  async function handleDeleteTag(id: number, count: number) {
    const ok = await confirmAction({
      title: t('settings.categoriesAndTags.delete_confirm', { count }),
      tone: 'danger',
    });
    if (!ok) return;
    deleteTag.mutate(id);
  }

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.categoriesAndTags.title')}</SectionTitle>

      {/* Relationship categories */}
      <p className="text-sm font-medium mb-3" style={{ color: 'var(--evo-fg-2)' }}>
        {t('settings.categoriesAndTags.rel_title')}
      </p>

      {relLoading && <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>}

      <div className="space-y-1.5 mb-3">
        {relCategories.map(cat => (
          <div
            key={cat.slug}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            {editingSlug === cat.slug ? (
              /* ── inline edit mode ── */
              <div className="flex items-center gap-2 flex-1">
                <div style={{ flexShrink: 0 }}>
                  <Input type="color" value={editColor} onChange={v => setEditColor(v)} size="sm" />
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <Input value={editLabelEs} onChange={v => setEditLabelEs(v)} placeholder={t('settings.categoriesAndTags.label_es_label')} size="sm" fullWidth />
                  <Input value={editLabelEn} onChange={v => setEditLabelEn(v)} placeholder={t('settings.categoriesAndTags.label_en_label')} size="sm" fullWidth />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="solid"
                    tone="cyan"
                    size="sm"
                    disabled={updateRelCat.isPending}
                    onClick={() => {
                      if (!editLabelEs.trim() || !editLabelEn.trim()) {
                        alert(t('settings.categoriesAndTags.rename_error_empty'));
                        return;
                      }
                      updateRelCat.mutate(
                        { slug: cat.slug, patch: { labelEs: editLabelEs, labelEn: editLabelEn, color: editColor } },
                        { onSuccess: () => setEditingSlug(null) },
                      );
                    }}
                  >
                    {t('settings.categoriesAndTags.save_btn')}
                  </Button>
                  <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setEditingSlug(null)}>
                    {t('settings.categoriesAndTags.cancel_btn')}
                  </Button>
                </div>
              </div>
            ) : (
              /* ── display mode ── */
              <>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full shrink-0"
                    style={{ background: cat.color }}
                  />
                  <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{cat.labelEs}</span>
                  <span className="text-xs font-mono" style={{ color: 'var(--evo-fg-3)' }}>{cat.slug}</span>
                  {cat.isSystem ? (
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--evo-cyan)22', color: 'var(--evo-cyan)', border: '1px solid var(--evo-cyan)44' }}>
                      {t('settings.categoriesAndTags.system_badge')}
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.categoriesAndTags.contacts_using', { count: cat.count })}
                  </span>
                  {!cat.isSystem && (
                    <>
                      <Button variant="outline" tone="cyan" size="sm" onClick={() => {
                          setEditingSlug(cat.slug);
                          setEditLabelEs(cat.labelEs);
                          setEditLabelEn(cat.labelEn);
                          setEditColor(cat.color);
                        }}>
                        {t('settings.categoriesAndTags.edit_btn')}
                      </Button>
                      <Button variant="ghost" tone="danger" size="sm" disabled={deleteRelCat.isPending} onClick={() => { void handleDeleteCat(cat.slug, cat.isSystem, cat.count); }}>
                        {t('settings.categoriesAndTags.delete_btn')}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {showNewCat ? (
        <form onSubmit={handleCreateCat} className="flex items-end gap-2 p-3 rounded mb-6"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.slug_label')}</label>
            <Input value={newCat.slug} onChange={v => setNewCat(f => ({ ...f, slug: v.replace(/\s+/g, '_').toLowerCase() }))} placeholder={t('settings.categoriesAndTags.slug_placeholder')} required size="sm" fullWidth />
          </div>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.label_label')}</label>
            <Input value={newCat.labelEs} onChange={v => setNewCat(f => ({ ...f, labelEs: v }))} placeholder={t('settings.categoriesAndTags.label_placeholder')} required size="sm" fullWidth />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.color_label')}</label>
            <Input type="color" value={newCat.color} onChange={color => setNewCat(f => ({ ...f, color }))} size="sm" />
          </div>
          <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createRelCat.isPending}>
            {t('settings.categoriesAndTags.create_btn')}
          </Button>
          <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setShowNewCat(false)}>
            {t('settings.categoriesAndTags.cancel_btn')}
          </Button>
        </form>
      ) : (
        <div style={{ marginBottom: '24px' }}>
          <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNewCat(true)}>
            {t('settings.categoriesAndTags.new_category_btn')}
          </Button>
        </div>
      )}

      {/* Tags */}
      <p className="text-sm font-medium mb-3" style={{ color: 'var(--evo-fg-2)' }}>
        {t('settings.categoriesAndTags.tags_title')}
      </p>

      {tagsLoading && <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>}

      <div className="space-y-1.5 mb-3">
        {tags.length === 0 && !tagsLoading && (
          <p className="text-xs mb-2" style={{ color: 'var(--evo-fg-3)' }}>
            {t('contacts.tag.none')}
          </p>
        )}
        {tags.map(tag => (
          <div
            key={tag.id}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: tag.color ?? '#888888' }} />
              <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{tag.label}</span>
              <span className="text-xs font-mono" style={{ color: 'var(--evo-fg-3)' }}>{tag.slug}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                {t('settings.categoriesAndTags.contacts_using', { count: tag.count ?? 0 })}
              </span>
              <Button variant="ghost" tone="danger" size="sm" disabled={deleteTag.isPending} onClick={() => { void handleDeleteTag(tag.id, tag.count ?? 0); }}>
                {t('settings.categoriesAndTags.delete_btn')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {showNewTag ? (
        <form onSubmit={handleCreateTag} className="flex items-end gap-2 p-3 rounded"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.slug_label')}</label>
            <Input value={newTag.slug} onChange={v => setNewTag(f => ({ ...f, slug: v.replace(/\s+/g, '_').toLowerCase() }))} placeholder="ej. vip" required size="sm" fullWidth />
          </div>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.label_label')}</label>
            <Input value={newTag.label} onChange={v => setNewTag(f => ({ ...f, label: v }))} placeholder="VIP" required size="sm" fullWidth />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.color_label')}</label>
            <Input type="color" value={newTag.color} onChange={color => setNewTag(f => ({ ...f, color }))} size="sm" />
          </div>
          <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createTag.isPending}>
            {t('settings.categoriesAndTags.create_btn')}
          </Button>
          <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setShowNewTag(false)}>
            {t('settings.categoriesAndTags.cancel_btn')}
          </Button>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNewTag(true)}>
          {t('settings.categoriesAndTags.new_tag_btn')}
        </Button>
      )}

      {/* Email categories — antes CATEGORY_COLORS hardcodeado en EmailPage.tsx */}
      <p className="text-sm font-medium mb-3 mt-8" style={{ color: 'var(--evo-fg-2)' }}>
        {t('settings.categoriesAndTags.email_categories_title')}
      </p>

      {emailCategoriesLoading && <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>}

      <div className="space-y-1.5 mb-3">
        {emailCategories.map(cat => (
          <div
            key={cat.id}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            {editingEmailCatId === cat.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input type="color" value={editEmailCatColor} onChange={v => setEditEmailCatColor(v)} size="sm" />
                <Input value={editEmailCatName} onChange={v => setEditEmailCatName(v)} size="sm" fullWidth />
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="solid" tone="cyan" size="sm"
                    disabled={updateEmailCat.isPending}
                    onClick={() => {
                      if (!editEmailCatName.trim()) return;
                      updateEmailCat.mutate(
                        { id: cat.id, patch: { name: editEmailCatName, color: editEmailCatColor } },
                        { onSuccess: () => setEditingEmailCatId(null) },
                      );
                    }}
                  >
                    {t('settings.categoriesAndTags.save_btn')}
                  </Button>
                  <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setEditingEmailCatId(null)}>
                    {t('settings.categoriesAndTags.cancel_btn')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: cat.color }} />
                  <span className="text-sm" style={{ color: 'var(--evo-fg-2)' }}>{cat.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" tone="cyan" size="sm" onClick={() => {
                    setEditingEmailCatId(cat.id);
                    setEditEmailCatName(cat.name);
                    setEditEmailCatColor(cat.color);
                  }}>
                    {t('settings.categoriesAndTags.edit_btn')}
                  </Button>
                  <Button variant="ghost" tone="danger" size="sm" disabled={deleteEmailCat.isPending} onClick={() => { void handleDeleteEmailCat(cat.id); }}>
                    {t('settings.categoriesAndTags.delete_btn')}
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {showNewEmailCat ? (
        <form onSubmit={handleCreateEmailCat} className="flex items-end gap-2 p-3 rounded"
          style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex-1">
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.name_label')}</label>
            <Input value={newEmailCat.name} onChange={v => setNewEmailCat(f => ({ ...f, name: v }))} required size="sm" fullWidth />
          </div>
          <div>
            <label className="block text-xs mb-1" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.categoriesAndTags.color_label')}</label>
            <Input type="color" value={newEmailCat.color} onChange={color => setNewEmailCat(f => ({ ...f, color }))} size="sm" />
          </div>
          <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createEmailCat.isPending}>
            {t('settings.categoriesAndTags.create_btn')}
          </Button>
          <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setShowNewEmailCat(false)}>
            {t('settings.categoriesAndTags.cancel_btn')}
          </Button>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNewEmailCat(true)}>
          {t('settings.categoriesAndTags.new_category_btn')}
        </Button>
      )}
    </div>
  );
}

// ── Section: Lead Engine ──────────────────────────────────────────────────────

const DEFAULT_LEAD_ENGINE_SIGNATURE = `<table cellpadding="0" cellspacing="0" border="0" width="100%" style="font-family: Arial, sans-serif; max-width: 480px;">
  <tr>
    <td style="background-color: #0A0A0A; padding: 24px; border-radius: 8px;">
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td style="padding-right: 16px; vertical-align: middle; width: 80px;">
            <img src="https://evocreativestudio.com/images/evo_logo_700x360.png" alt="Evo Creative Studio" width="80" style="display: block; max-width: 80px; height: auto;" />
          </td>
          <td style="border-left: 2px solid #00fac8; padding-left: 16px; vertical-align: middle;">
            <div style="font-weight: bold; font-size: 15px; color: #ffffff; margin-bottom: 2px;">Eduardo Carrasco</div>
            <div style="color: #999999; font-size: 13px; margin-bottom: 8px;">Creative Director</div>
            <div style="margin-bottom: 4px;">
              <a href="mailto:info@evocreativestudio.com" style="color: #00fac8; text-decoration: none; font-size: 13px;">info@evocreativestudio.com</a>
            </div>
            <div style="margin-bottom: 12px;">
              <a href="https://evocreativestudio.com" style="color: #00fac8; text-decoration: none; font-size: 13px;">evocreativestudio.com</a>
            </div>
            <div>
              <a href="https://instagram.com/evocreativeonline" style="display: inline-block; margin-right: 8px; text-decoration: none;">
                <img src="https://cdn.simpleicons.org/instagram/00fac8" alt="Instagram" width="20" height="20" style="display: block; border: 0;" />
              </a>
              <a href="https://facebook.com/evocreativeonline" style="display: inline-block; text-decoration: none;">
                <img src="https://cdn.simpleicons.org/facebook/00fac8" alt="Facebook" width="20" height="20" style="display: block; border: 0;" />
              </a>
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

// ── Pipeline helpers ─────────────────────────────────────────────────────────

function pipelineRelativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso.endsWith('Z') ? iso : iso + 'Z').getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

// ── Lead Pipeline Card ────────────────────────────────────────────────────────

function LeadPipelineCard({
  pipeline,
  hasOverlap,
}: {
  pipeline: LeadPipeline;
  hasOverlap: boolean;
}) {
  const { t } = useTranslation();
  const update = useUpdateLeadPipeline();
  const del = useDeleteLeadPipeline();
  const runNow = useRunPipelineNow();
  const toast = useToast();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const [justTriggered, setJustTriggered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [name, setName]             = useState(pipeline.name);
  const [enabled, setEnabled]       = useState(pipeline.enabled === 1);
  const [categories, setCategories] = useState<string[]>(pipeline.categories);
  const [zones, setZones]           = useState<ScrapeZone[]>(pipeline.zones);
  const [zoneInput, setZoneInput]   = useState('');
  const [capPerRun, setCapPerRun]   = useState(pipeline.capPerRun);
  const [cronHour, setCronHour]     = useState(pipeline.cronHour);
  const [cronMinute, setCronMinute] = useState(pipeline.cronMinute);
  const [intent, setIntent]         = useState<'audit' | 'demo'>((pipeline.intent as 'audit' | 'demo') || 'audit');
  const [autoScrape, setAutoScrapeRaw]   = useState(pipeline.autoScrape === 1);
  const [autoEnrich, setAutoEnrichRaw]   = useState(pipeline.autoEnrich === 1);
  const [autoAnalysis, setAutoAnalysisRaw] = useState(pipeline.autoAnalysis === 1);
  const [autoDraft, setAutoDraftRaw]     = useState(pipeline.autoDraft === 1);
  const [autoSend, setAutoSend]          = useState(pipeline.autoSend === 1);
  const [saved, setSaved]           = useState(false);
  const [saveError, setSaveError]   = useState<string | null>(null);

  useEffect(() => {
    setName(pipeline.name);
    setEnabled(pipeline.enabled === 1);
    setCategories(pipeline.categories);
    setZones(pipeline.zones);
    setCapPerRun(pipeline.capPerRun);
    setCronHour(pipeline.cronHour);
    setCronMinute(pipeline.cronMinute);
    setIntent((pipeline.intent as 'audit' | 'demo') || 'audit');
    setAutoScrapeRaw(pipeline.autoScrape === 1);
    setAutoEnrichRaw(pipeline.autoEnrich === 1);
    setAutoAnalysisRaw(pipeline.autoAnalysis === 1);
    setAutoDraftRaw(pipeline.autoDraft === 1);
    setAutoSend(pipeline.autoSend === 1);
  }, [pipeline]);

  const isRunning = justTriggered || pipeline.lastRunStatus === 'running' || pipeline.lastRunStatus === 'queued';

  useEffect(() => {
    if (justTriggered && pipeline.lastRunStatus && pipeline.lastRunStatus !== 'queued' && pipeline.lastRunStatus !== 'running') {
      setJustTriggered(false);
    }
  }, [pipeline.lastRunStatus, justTriggered]);

  function setAutoScrape(v: boolean) {
    setAutoScrapeRaw(v);
    if (!v) { setAutoEnrichRaw(false); setAutoAnalysisRaw(false); setAutoDraftRaw(false); setAutoSend(false); }
  }
  function setAutoEnrich(v: boolean) {
    setAutoEnrichRaw(v);
    if (!v) { setAutoAnalysisRaw(false); setAutoDraftRaw(false); setAutoSend(false); }
  }
  function setAutoAnalysis(v: boolean) {
    setAutoAnalysisRaw(v);
    if (!v) { setAutoDraftRaw(false); setAutoSend(false); }
  }
  function setAutoDraft(v: boolean) {
    setAutoDraftRaw(v);
    if (!v) setAutoSend(false);
  }

  function addZone() {
    const trimmed = zoneInput.trim();
    if (!trimmed) return;
    const parts = trimmed.split(',').map(p => p.trim());
    setZones(z => [...z, { city: parts[0] ?? trimmed, state: parts[1] ?? '' }]);
    setZoneInput('');
  }

  function handleSave() {
    setSaveError(null);
    const body: LeadPipelineInput = {
      name, enabled, categories, zones, capPerRun, cronHour, cronMinute, intent,
      autoScrape, autoEnrich, autoAnalysis, autoDraft, autoSend,
    };
    update.mutate({ id: pipeline.id, ...body }, {
      onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); },
      onError: (err) => setSaveError((err).message),
    });
  }

  async function handleDelete() {
    const ok = await confirmAction({ title: t('settings.pipelines.deleteConfirm'), confirmLabel: t('common.delete') });
    if (!ok) return;
    del.mutate(pipeline.id);
  }

  const hourOptions   = Array.from({ length: 24 }, (_, i) => ({ value: i, label: String(i).padStart(2, '0') + ':00' }));
  const minuteOptions = [0, 15, 30, 45].map(m => ({ value: m, label: String(m).padStart(2, '0') }));

  const steps = [
    { key: 'autoScrape',   label: t('settings.pipelines.steps.autoScrape'),   hint: t('settings.pipelines.steps.scrapeHint'),   value: autoScrape,   onChange: setAutoScrape,   disabled: false },
    { key: 'autoEnrich',   label: t('settings.pipelines.steps.autoEnrich'),   hint: t('settings.pipelines.steps.enrichHint'),   value: autoEnrich,   onChange: setAutoEnrich,   disabled: !autoScrape },
    { key: 'autoAnalysis', label: t('settings.pipelines.steps.autoAnalysis'), hint: t('settings.pipelines.steps.analysisHint'), value: autoAnalysis, onChange: setAutoAnalysis, disabled: !autoEnrich },
    { key: 'autoDraft',    label: t('settings.pipelines.steps.autoDraft'),    hint: t('settings.pipelines.steps.draftHint'),    value: autoDraft,    onChange: setAutoDraft,    disabled: !autoAnalysis },
    { key: 'autoSend',     label: t('settings.pipelines.steps.autoSend'),     hint: t('settings.pipelines.steps.sendHint'),     value: autoSend,     onChange: setAutoSend,     disabled: !autoDraft },
  ];

  return (
    <>
      <details
        className="pipeline-card"
        onToggle={e => setIsOpen((e.currentTarget as HTMLDetailsElement).open)}
      >
        <summary className="pipeline-summary">
          {/* Left: name + status badges */}
          <div className="pipeline-summary-left">
            <span className="pipeline-name">{pipeline.name}</span>
            <Badge variant="soft" tone={enabled ? 'cyan' : 'muted'} size="sm">
              {enabled ? t('settings.pipelines.active') : t('settings.pipelines.inactive')}
            </Badge>
            {isRunning && (
              <Badge variant="soft" tone="cyan" size="sm">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', border: '1.5px solid currentColor', borderTopColor: 'transparent', display: 'inline-block', animation: 'evo-spin 0.7s linear infinite' }} />
                  {t('settings.pipelines.runningBadge')}
                </span>
              </Badge>
            )}
          </div>

          {/* Middle: schedule + last run */}
          <div className="pipeline-summary-meta">
            <span>{t('settings.pipelines.scheduledAt', { time: `${String(pipeline.cronHour).padStart(2, '0')}:${String(pipeline.cronMinute).padStart(2, '0')}` })}</span>
            {pipeline.lastRunAt && (
              <span>{t('settings.pipelines.lastRunAgo', { time: pipelineRelativeTime(pipeline.lastRunAt) })}</span>
            )}
          </div>

          {/* Right: toggle + delete (stopPropagation so they don't toggle the accordion) */}
          <div className="pipeline-summary-actions" onClick={e => e.stopPropagation()}>
            <Toggle checked={enabled} onChange={setEnabled} size="sm" />
            <Button variant="ghost" tone="danger" size="sm" onClick={() => void handleDelete()}>
              {t('common.delete')}
            </Button>
          </div>

          <span style={{ flexShrink: 0, marginLeft: 8, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : undefined, display: 'inline-flex' }}>
            <Icon name="chevron-down" size={14} />
          </span>
        </summary>

        <div className="pipeline-body">
          {pipeline.exhausted && (
            <div style={{ padding: '6px 10px', marginBottom: '12px', background: 'color-mix(in srgb, var(--evo-warn) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--evo-warn) 40%, transparent)', borderRadius: '6px' }}>
              <p style={{ fontFamily: 'var(--evo-font)', fontSize: '11px', color: 'var(--evo-warn)', margin: 0 }}>
                {t('settings.pipelines.exhaustedWarning')}
              </p>
            </div>
          )}
          {hasOverlap && (
            <div style={{ padding: '6px 10px', marginBottom: '12px', background: 'color-mix(in srgb, var(--evo-warn) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--evo-warn) 40%, transparent)', borderRadius: '6px' }}>
              <p style={{ fontFamily: 'var(--evo-font)', fontSize: '11px', color: 'var(--evo-warn)', margin: 0 }}>
                {t('settings.pipelines.overlapWarning')}
              </p>
            </div>
          )}

          {/* Name (editable) */}
          <div style={{ marginBottom: '12px' }}>
            <Input value={name} onChange={v => setName(v)} placeholder={t('settings.pipelines.namePlaceholder')} size="sm" fullWidth />
          </div>

          <Field label={t('settings.scrapeSchedule.categoriesLabel')}>
            <Dropdown<string>
              multi
              value={categories}
              options={SCRAPE_CATEGORY_OPTIONS}
              onChange={setCategories}
              placeholder={t('settings.scrapeSchedule.categoriesPlaceholder')}
              size="sm"
            />
          </Field>

          <Field label={t('settings.scrapeSchedule.zonesLabel')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {zones.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {zones.map((z, i) => (
                    <Badge key={i} variant="soft" tone="cyan" size="sm"
                      rightIcon={<Icon name="close" size={9} />}
                      onClick={() => setZones(zs => zs.filter((_, idx) => idx !== i))}>
                      {z.city}{z.state ? `, ${z.state}` : ''}
                    </Badge>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ width: '200px' }}>
                  <Input value={zoneInput} onChange={v => setZoneInput(v)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addZone(); } }} placeholder={t('settings.scrapeSchedule.zonePlaceholder')} size="sm" fullWidth />
                </div>
                <Button type="button" variant="outline" tone="muted" size="sm" onClick={addZone}>
                  {t('settings.scrapeSchedule.addZone')}
                </Button>
              </div>
            </div>
          </Field>

          <Field label={t('settings.scrapeSchedule.capLabel')} hint={t('settings.scrapeSchedule.capHint')}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <Input
                type="number"
                value={String(capPerRun)}
                onChange={v => setCapPerRun(Math.min(50, Math.max(1, parseInt(v) || 1)))}
                size="sm"
              />
              <span style={{ color: 'var(--evo-fg-4)', fontSize: '11px' }}>{t('settings.scrapeSchedule.timeLabel')}</span>
              <Dropdown<number> value={cronHour} options={hourOptions} onChange={setCronHour} size="sm" />
              <span style={{ color: 'var(--evo-fg-3)', fontSize: '13px' }}>:</span>
              <Dropdown<number> value={cronMinute} options={minuteOptions} onChange={setCronMinute} size="sm" />
            </div>
          </Field>

          <Field label={t('settings.scrapeSchedule.intentLabel')}>
            <div style={{ display: 'flex', gap: '16px' }}>
              {(['audit', 'demo'] as const).map(mode => (
                <Radio key={mode} value={mode} checked={intent === mode} onChange={() => setIntent(mode)}
                  label={t(`settings.scrapeSchedule.intent.${mode}`)} />
              ))}
            </div>
          </Field>

          {/* 5-step automation toggles */}
          <div style={{ marginTop: '14px', padding: '12px', background: 'var(--evo-bg-3)', borderRadius: '6px' }}>
            <p style={{ fontFamily: 'var(--evo-font)', fontSize: '10px', fontWeight: 500, color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>
              {t('settings.pipelines.steps.title')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {steps.map(step => (
                <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', opacity: step.disabled ? 0.4 : 1 }}>
                  <div>
                    <p style={{ fontFamily: 'var(--evo-font)', fontSize: '13px', color: 'var(--evo-fg-2)', margin: 0 }}>{step.label}</p>
                    <p style={{ fontFamily: 'var(--evo-font)', fontSize: '11px', color: 'var(--evo-fg-4)', margin: '2px 0 0' }}>{step.hint}</p>
                  </div>
                  <Toggle checked={step.value} onChange={step.disabled ? () => undefined : step.onChange} size="sm" />
                </div>
              ))}
            </div>
          </div>

          {/* Last run */}
          <div style={{ marginTop: '10px', padding: '8px 12px', background: 'var(--evo-bg-3)', borderRadius: '6px' }}>
            <p style={{ fontFamily: 'var(--evo-font)', fontSize: '10px', fontWeight: 500, color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' }}>
              {t('settings.pipelines.lastRun.title')}
            </p>
            {pipeline.lastRunAt ? (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-fg-2)' }}>
                  {new Date(pipeline.lastRunAt).toLocaleString()}
                </span>
                {pipeline.lastRunStatus && (
                  <Badge variant="soft" tone={pipeline.lastRunStatus === 'success' ? 'success' : 'danger'} size="sm">
                    {pipeline.lastRunStatus}
                  </Badge>
                )}
                {pipeline.lastRunCount != null && (
                  <span style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-fg-3)' }}>
                    {pipeline.lastRunCount} leads
                  </span>
                )}
              </div>
            ) : (
              <span style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-fg-4)' }}>
                {t('settings.pipelines.lastRun.never')}
              </span>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
            <Button
              type="button"
              variant="outline"
              tone="muted"
              size="sm"
              loading={runNow.isPending}
              onClick={() => runNow.mutate(pipeline.id, {
                onSuccess: (data) => {
                  setJustTriggered(true);
                  toast.success(t('settings.pipelines.runNowSuccess', { name, count: data.runsQueued }));
                },
                onError: (err) => {
                  toast.error(t('settings.pipelines.runNowError', { message: (err).message }));
                },
              })}
            >
              {runNow.isPending ? t('settings.pipelines.runNowPending') : t('settings.pipelines.runNow')}
            </Button>
            <Button size="sm" tone="cyan" variant={saved ? 'outline' : 'solid'} loading={update.isPending} onClick={handleSave}>{update.isPending ? t('settings.savingBtn') : saved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
          </div>
          {saveError && (
            <p style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-danger)', marginTop: '8px' }}>{saveError}</p>
          )}
        </div>
      </details>
      {confirmDialog}
    </>
  );
}

// ── Lead Pipelines List ───────────────────────────────────────────────────────

function LeadPipelinesList({ globalDailyCap }: { globalDailyCap: number }) {
  const { t } = useTranslation();
  const { data: pipelines = [], isLoading } = useLeadPipelines();
  const create = useCreateLeadPipeline();
  const [showAddModal, setShowAddModal] = useState(false);

  // Overlap detection: count enabled pipelines per cronHour:cronMinute
  const scheduleCount = new Map<string, number>();
  for (const p of pipelines) {
    if (p.enabled === 1) {
      const key = `${p.cronHour}:${p.cronMinute}`;
      scheduleCount.set(key, (scheduleCount.get(key) ?? 0) + 1);
    }
  }

  const autoSendCount = pipelines.filter(p => p.enabled === 1 && p.autoSend === 1).length;

  if (isLoading) return null;

  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <p style={{ fontFamily: 'var(--evo-font)', fontSize: '10px', fontWeight: 500, color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.6px', margin: 0 }}>
          {t('settings.pipelines.listTitle')}
        </p>
        <Button type="button" variant="outline" tone="muted" size="sm" disabled={create.isPending} onClick={() => setShowAddModal(true)}>
          + {t('settings.pipelines.addPipeline')}
        </Button>
      </div>

      {autoSendCount > 1 && (
        <div style={{ padding: '8px 12px', marginBottom: '12px', background: 'color-mix(in srgb, var(--evo-warn) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--evo-warn) 40%, transparent)', borderRadius: '6px' }}>
          <p style={{ fontFamily: 'var(--evo-font)', fontSize: '12px', color: 'var(--evo-warn)', margin: 0 }}>
            {t('settings.pipelines.capWarning', { count: autoSendCount, cap: globalDailyCap })}
          </p>
        </div>
      )}

      {pipelines.length === 0 && (
        <EmptyState size="sm" reserveSpace title={t('settings.pipelines.empty')} />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {pipelines.map(pipeline => (
          <LeadPipelineCard
            key={pipeline.id}
            pipeline={pipeline}
            hasOverlap={(scheduleCount.get(`${pipeline.cronHour}:${pipeline.cronMinute}`) ?? 0) > 1 && pipeline.enabled === 1}
          />
        ))}
      </div>

      {showAddModal && (
        <PipelineEditorModal
          onSave={data => create.mutate(data, { onSuccess: () => setShowAddModal(false) })}
          onCancel={() => setShowAddModal(false)}
          saving={create.isPending}
          error={create.isError ? (create.error).message : null}
        />
      )}
    </div>
  );
}

// ── Scraping Telemetry Card ───────────────────────────────────────────────────

interface TelemetrySummary {
  total_attempts: number;
  success_rate: number;
  by_status: Record<string, number>;
  last_failure: { timestamp: string; lead_id: string | null; error_message: string | null } | null;
}

function ScrapingTelemetryCard() {
  const { t } = useTranslation();
  const { data } = useQuery<TelemetrySummary>({
    queryKey: ['scraping-telemetry-summary'],
    queryFn: () => api.get<TelemetrySummary>('/api/scraping-telemetry/summary'),
    refetchInterval: 60_000,
    staleTime: 0,
  });

  if (!data) return null;

  const rateColor = data.success_rate >= 70
    ? 'var(--evo-cyan)'
    : data.success_rate >= 40
      ? '#fbbf24'
      : '#ef4444';

  return (
    <div className="mt-6 rounded-lg p-4" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
      <p className="text-xs font-medium mb-3" style={{ color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.88px' }}>
        {t('settings.leadEngine.scrapingTelemetry.title')}
      </p>
      <div className="flex items-center gap-4">
        <div>
          <p className="text-xs" style={{ color: 'var(--evo-fg-4)' }}>{t('settings.leadEngine.scrapingTelemetry.successRate')}</p>
          <p className="text-2xl font-bold mt-0.5" style={{ color: rateColor }}>{data.success_rate}%</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--evo-fg-4)' }}>
            {t('settings.leadEngine.scrapingTelemetry.totalAttempts', { count: data.total_attempts })}
          </p>
        </div>
        <div className="flex-1 space-y-1">
          {Object.entries(data.by_status).filter(([, v]) => v > 0).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2">
              <span className="text-xs w-28 truncate" style={{ color: 'var(--evo-fg-4)' }}>{key}</span>
              <span className="text-xs font-medium" style={{ color: 'var(--evo-fg-2)' }}>{val}</span>
            </div>
          ))}
        </div>
      </div>
      {data.success_rate < 40 && data.total_attempts > 0 && (
        <p className="text-xs mt-3 p-2 rounded" style={{ color: '#fbbf24', background: '#fbbf2410', border: '1px solid #fbbf2430' }}>
          {t('settings.leadEngine.scrapingTelemetry.lowRateWarning')}
        </p>
      )}
    </div>
  );
}

function LeadEngineSection() {
  const { t } = useTranslation();
  const { data: settings, isLoading } = useLeadEngineSettings();
  const update = useUpdateLeadEngineSettings();
  const { data: emailAccounts = [] } = useEmailAccounts();

  const [followupCount, setFollowupCount] = useState(2);
  const [delay1, setDelay1] = useState(3);
  const [delay2, setDelay2] = useState(7);
  const [delay3, setDelay3] = useState(14);
  const [signatureHtml, setSignatureHtml] = useState('');
  const [fromAccount, setFromAccount] = useState('');
  const [dailyCap, setDailyCap] = useState(10);
  const [autoSendStartHour, setAutoSendStartHour] = useState(9);
  const [autoSendEndHour, setAutoSendEndHour] = useState(17);
  const [autoCloseGraceDays, setAutoCloseGraceDays] = useState(2);
  const [previewSig, setPreviewSig] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setFollowupCount(settings.followupCount);
    setDelay1(settings.followupDelay1);
    setDelay2(settings.followupDelay2);
    setDelay3(settings.followupDelay3);
    setSignatureHtml(settings.signatureHtml);
    setFromAccount(settings.fromAccount);
    setDailyCap(settings.dailyCap);
    setAutoSendStartHour(settings.autoSendStartHour);
    setAutoSendEndHour(settings.autoSendEndHour);
    setAutoCloseGraceDays(settings.autoCloseGraceDays ?? 2);
  }, [settings]);

  function handleSave() {
    update.mutate(
      { followupCount, followupDelay1: delay1, followupDelay2: delay2, followupDelay3: delay3, signatureHtml, fromAccount, dailyCap, autoSendStartHour, autoSendEndHour, autoCloseGraceDays },
      { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } },
    );
  }

  const delayRows = [
    { label: t('settings.leadEngine.delay1Label'), value: delay1, set: setDelay1 },
    ...(followupCount >= 2 ? [{ label: t('settings.leadEngine.delay2Label'), value: delay2, set: setDelay2 }] : []),
    ...(followupCount >= 3 ? [{ label: t('settings.leadEngine.delay3Label'), value: delay3, set: setDelay3 }] : []),
  ];

  if (isLoading) {
    return <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>;
  }

  return (
    <div>
      <SectionTitle>{t('settings.leadEngine.title')}</SectionTitle>

      {/* Global draft config */}
      <div className="pt-2 pb-2" style={{ marginBottom: '4px' }}>
        <p className="text-xs font-medium" style={{ color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.88px' }}>
          {t('settings.leadEngine.globalDraftTitle')}
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--evo-fg-4)' }}>
          {t('settings.leadEngine.globalDraftDesc')}
        </p>
      </div>

      {/* Follow-up count */}
      <Field label={t('settings.leadEngine.followupCountLabel')} hint={t('settings.leadEngine.followupCountHint')}>
        <Dropdown<number>
          value={followupCount}
          options={[1, 2, 3].map(n => ({ value: n, label: t('settings.leadEngine.followupCountOption', { count: n }) }))}
          onChange={setFollowupCount}
          size="sm"
        />
      </Field>

      {/* Delays */}
      {delayRows.map(({ label, value, set }) => (
        <Field key={label} label={label} hint={t('settings.leadEngine.delayHint')}>
          <div className="flex items-center gap-2">
            <div style={{ width: '80px' }}>
              <Input type="number" min={1} max={90} value={String(value)} onChange={v => set(Math.max(1, parseInt(v) || 1))} size="sm" fullWidth />
            </div>
            <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.leadEngine.days')}</span>
          </div>
        </Field>
      ))}

      {/* HTML Signature */}
      <Field label={t('settings.leadEngine.signatureLabel')} hint={t('settings.leadEngine.signatureHint')}>
        <div className="space-y-2">
          <Textarea
            value={signatureHtml}
            onChange={v => setSignatureHtml(v)}
            rows={6}
            resize="vertical"
            placeholder={t('settings.leadEngine.signaturePlaceholder')}
          />
          <div className="flex items-center gap-2">
            <Button variant="outline" tone={previewSig ? 'cyan' : 'muted'} size="sm" onClick={() => setPreviewSig(s => !s)}>
              {previewSig ? t('settings.leadEngine.hidePreview') : t('settings.leadEngine.showPreview')}
            </Button>
            <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => setSignatureHtml(DEFAULT_LEAD_ENGINE_SIGNATURE)}>
              {t('settings.leadEngine.resetSignature')}
            </Button>
          </div>
          {previewSig && signatureHtml && (
            <div
              className="rounded p-3"
              style={{ background: '#ffffff', border: '1px solid var(--evo-stroke)' }}
              dangerouslySetInnerHTML={{ __html: signatureHtml }}
            />
          )}
        </div>
      </Field>

      {/* From account */}
      <Field label={t('settings.leadEngine.fromAccountLabel')} hint={t('settings.leadEngine.fromAccountHint')}>
        <Dropdown<string>
          value={fromAccount || null}
          placeholder={t('settings.leadEngine.fromAccountPlaceholder')}
          options={emailAccounts.map(name => ({ value: name, label: name }))}
          onChange={setFromAccount}
          size="sm"
        />
      </Field>

      {/* Auto-send */}
      <div className="pt-6 pb-2">
        <p className="text-xs font-medium" style={{ color: 'var(--evo-fg-3)', textTransform: 'uppercase', letterSpacing: '0.88px' }}>
          {t('settings.leadEngine.autoSendTitle')}
        </p>
      </div>

      <Field label={t('settings.leadEngine.autoSendDailyCapLabel')} hint={t('settings.leadEngine.autoSendDailyCapHint')}>
        <div className="flex items-center gap-2">
          <div style={{ width: '80px' }}>
            <Input type="number" min={1} max={200} value={String(dailyCap)} onChange={v => setDailyCap(Math.max(1, parseInt(v) || 1))} size="sm" fullWidth />
          </div>
        </div>
      </Field>

      <Field label={t('settings.leadEngine.autoSendWindowLabel')} hint={t('settings.leadEngine.autoSendWindowHint')}>
        <div className="flex items-center gap-3">
          <Dropdown<number>
            value={autoSendStartHour}
            options={Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, '0')}:00` }))}
            onChange={setAutoSendStartHour}
            size="sm"
          />
          <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>→</span>
          <Dropdown<number>
            value={autoSendEndHour}
            options={Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${String(h).padStart(2, '0')}:00` }))}
            onChange={setAutoSendEndHour}
            size="sm"
          />
        </div>
      </Field>

      {/* Auto-close grace days */}
      <Field label={t('settings.leadEngine.autoCloseGraceDaysLabel')} hint={t('settings.leadEngine.autoCloseGraceDaysHint')}>
        <div className="flex items-center gap-2">
          <div style={{ width: '80px' }}>
            <Input type="number" min={1} max={30} value={String(autoCloseGraceDays)} onChange={v => setAutoCloseGraceDays(Math.min(30, Math.max(1, parseInt(v) || 1)))} size="sm" fullWidth />
          </div>
          <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.leadEngine.days')}</span>
        </div>
      </Field>

      {/* Reviews Scraping Telemetry */}
      <ScrapingTelemetryCard />

      {/* Save */}
      <div className="pt-4">
        <Button size="sm" tone="cyan" variant={saved ? 'outline' : 'solid'} loading={update.isPending} onClick={handleSave}>{update.isPending ? t('settings.savingBtn') : saved ? t('settings.savedBtn') : t('settings.saveBtn')}</Button>
      </div>

      <LeadPipelinesList globalDailyCap={dailyCap} />
    </div>
  );
}

// ── Section: Billing (company profile + accounts) ───────────────────────────────

// export: reusado tal cual (sin duplicar) por OnboardingPage.tsx — paso
// "datos de facturación" de la inducción (2026-08-05).
export function CompanyProfileForm() {
  const { t } = useTranslation();
  const { data: company, isLoading } = useCompany();
  const updateCompany = useUpdateCompany();
  const [form, setForm] = useState({
    name: '', legalName: '', address: '', taxId: '', email: '', phone: '',
    estimatePrefix: '', estimatePad: '3', invoicePrefix: '', invoicePad: '3',
    paymentTerms: '', legalFooter: '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!company) return;
    setForm({
      name: company.name ?? '',
      legalName: company.legalName ?? '',
      address: company.address ?? '',
      taxId: company.taxId ?? '',
      email: company.email ?? '',
      phone: company.phone ?? '',
      estimatePrefix: company.estimatePrefix,
      estimatePad: String(company.estimatePad),
      invoicePrefix: company.invoicePrefix,
      invoicePad: String(company.invoicePad),
      paymentTerms: company.paymentTerms ?? '',
      legalFooter: company.legalFooter ?? '',
    });
  }, [company]);

  function setField(key: keyof typeof form, val: string) {
    setForm(f => ({ ...f, [key]: val }));
    setSaved(false);
  }

  function handleSave() {
    updateCompany.mutate(
      {
        name: form.name,
        legalName: form.legalName || null,
        address: form.address || null,
        taxId: form.taxId || null,
        email: form.email || null,
        phone: form.phone || null,
        estimatePrefix: form.estimatePrefix,
        estimatePad: Math.max(1, Math.trunc(Number(form.estimatePad) || 3)),
        invoicePrefix: form.invoicePrefix,
        invoicePad: Math.max(1, Math.trunc(Number(form.invoicePad) || 3)),
        paymentTerms: form.paymentTerms || null,
        legalFooter: form.legalFooter || null,
      },
      { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } },
    );
  }

  function previewFolio(prefix: string, pad: string, nextSeq: number | undefined) {
    const p = Math.max(1, Math.trunc(Number(pad) || 3));
    return prefix + String(nextSeq ?? 1).padStart(p, '0');
  }

  if (isLoading) return <LoadingState size="sm" />;

  return (
    <div>
      <SectionTitle>{t('billing.company.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.company.hint')}</p>

      <Field label={t('billing.company.name')}>
        <Input value={form.name} onChange={v => setField('name', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.legalName')}>
        <Input value={form.legalName} onChange={v => setField('legalName', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.address')}>
        <Textarea value={form.address} onChange={v => setField('address', v)} rows={2} fullWidth />
      </Field>
      <Field label={t('billing.company.taxId')}>
        <Input value={form.taxId} onChange={v => setField('taxId', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.email')}>
        <Input type="email" value={form.email} onChange={v => setField('email', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.phone')}>
        <Input value={form.phone} onChange={v => setField('phone', v)} size="sm" fullWidth />
      </Field>
      <Field label={t('billing.company.paymentTerms')} hint={t('billing.company.paymentTermsHint')}>
        <Textarea value={form.paymentTerms} onChange={v => setField('paymentTerms', v)} rows={2} fullWidth />
      </Field>
      <Field label={t('billing.company.legalFooter')} hint={t('billing.company.legalFooterHint')}>
        <Textarea value={form.legalFooter} onChange={v => setField('legalFooter', v)} rows={2} fullWidth />
      </Field>

      <SectionTitle>{t('billing.numbering.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.numbering.hint')}</p>

      <Field label={t('billing.numbering.estimatePrefix')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input value={form.estimatePrefix} onChange={v => setField('estimatePrefix', v)} size="sm" fullWidth /></div>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input type="number" value={form.estimatePad} onChange={v => setField('estimatePad', v)} size="sm" fullWidth /></div>
          <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
            {t('billing.numbering.nextPreview', { folio: previewFolio(form.estimatePrefix, form.estimatePad, company?.estimateNextSeq) })}
          </span>
        </div>
      </Field>

      <Field label={t('billing.numbering.invoicePrefix')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input value={form.invoicePrefix} onChange={v => setField('invoicePrefix', v)} size="sm" fullWidth /></div>
          <div style={{ width: 'var(--evo-field-w-xs)' }}><Input type="number" value={form.invoicePad} onChange={v => setField('invoicePad', v)} size="sm" fullWidth /></div>
          <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
            {t('billing.numbering.nextPreview', { folio: previewFolio(form.invoicePrefix, form.invoicePad, company?.invoiceNextSeq) })}
          </span>
        </div>
      </Field>

      <div style={{ marginTop: 'var(--evo-space-4)' }}>
        <Button size="sm" tone="cyan" variant={saved ? 'outline' : 'solid'} loading={updateCompany.isPending} onClick={handleSave}>
          {updateCompany.isPending ? t('settings.savingBtn') : saved ? t('settings.savedBtn') : t('settings.saveBtn')}
        </Button>
      </div>
    </div>
  );
}

function AccountsList() {
  const { t } = useTranslation();
  const { data: company } = useCompany();
  const { data: accountsData, isLoading } = useAccounts(true);
  const accounts = accountsData?.accounts ?? [];
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', currency: '' });

  async function handleDelete(id: number) {
    const ok = await confirmAction({ title: t('billing.accounts.deleteConfirm'), confirmLabel: t('common.delete') });
    if (!ok) return;
    deleteAccount.mutate(id);
  }

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!company) return;
    createAccount.mutate(
      { companyId: company.id, name: form.name, currency: form.currency },
      { onSuccess: () => { setForm({ name: '', currency: '' }); setShowForm(false); } },
    );
  }

  return (
    <div>
      <SectionTitle>{t('billing.accounts.title')}</SectionTitle>
      <p className="text-xs mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.accounts.hint')}</p>

      {isLoading && <LoadingState size="sm" />}

      {accounts.length === 0 && !isLoading && (
        <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('billing.accounts.empty')}</p>
      )}

      <div className="space-y-2 mb-4">
        {accounts.map(acc => (
          <div
            key={acc.id}
            className="flex items-center justify-between px-3 py-2 rounded"
            style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
          >
            <div className="flex items-center gap-3">
              <span className="text-sm" style={{ color: 'var(--evo-fg-1)' }}>{acc.name}</span>
              <Pill tone="cyan" variant="outline" size="sm">{acc.currency.toUpperCase()}</Pill>
              <Pill tone={acc.isActive ? 'success' : 'muted'} variant="subtle" size="sm">
                {acc.isActive ? t('billing.accounts.active') : t('billing.accounts.inactive')}
              </Pill>
            </div>
            {acc.hasDocuments ? (
              <Button
                variant="outline"
                tone={acc.isActive ? 'muted' : 'cyan'}
                size="sm"
                loading={updateAccount.isPending}
                onClick={() => updateAccount.mutate({ id: acc.id, isActive: !acc.isActive })}
              >
                {acc.isActive ? t('billing.accounts.retire') : t('billing.accounts.reactivate')}
              </Button>
            ) : (
              <Button
                variant="outline"
                tone="danger"
                size="sm"
                loading={deleteAccount.isPending}
                onClick={() => void handleDelete(acc.id)}
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
        ))}
      </div>

      {showForm ? (
        <form onSubmit={handleAdd} className="space-y-3 p-4 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('billing.accounts.name')}
            </label>
            <Input
              value={form.name}
              onChange={v => setForm(f => ({ ...f, name: v }))}
              placeholder={t('billing.accounts.namePlaceholder')}
              required
              size="sm"
              fullWidth
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs" style={{ color: 'var(--evo-fg-3)', width: '160px', flexShrink: 0 }}>
              {t('billing.accounts.currency')}
            </label>
            <Input
              value={form.currency}
              onChange={v => setForm(f => ({ ...f, currency: v.toLowerCase() }))}
              placeholder={t('billing.accounts.currencyPlaceholder')}
              required
              size="sm"
              fullWidth
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createAccount.isPending}>
              {t('billing.accounts.addAccountBtn')}
            </Button>
            <Button type="button" variant="outline" tone="muted" size="sm" onClick={() => setShowForm(false)}>
              {t('billing.accounts.cancelBtn')}
            </Button>
          </div>
        </form>
      ) : (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowForm(true)} disabled={!company}>
          {t('billing.accounts.addAccount')}
        </Button>
      )}
      {confirmDialog}
    </div>
  );
}

function BillingSection() {
  return (
    <div className="space-y-8">
      <CompanyProfileForm />
      <AccountsList />
    </div>
  );
}

// Ventana para considerar "conectado" — no hay heartbeat dedicado, se infiere
// de la última llamada real registrada en audit_log (routes/external.ts).
// 15 min es generoso frente a cualquier polling razonable de OpenClaw sin
// marcar "desconectado" por un rato sin actividad normal.
const OPENCLAW_RECENT_MS = 15 * 60 * 1000;

function OpenClawSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useOpenClawStatus();
  const issueToken = useIssueOpenClawToken();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const toast = useToast();
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  const isConnected = data?.lastSeenAt != null && (Date.now() - parseServerDate(data.lastSeenAt).getTime()) < OPENCLAW_RECENT_MS;

  async function handleIssueToken() {
    const ok = await confirmAction({
      title: data?.tokenIssued ? t('settings.openclaw.regenerate_confirm') : t('settings.openclaw.generate_confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    issueToken.mutate(undefined, { onSuccess: r => setRevealedToken(r.token) });
  }

  function handleCopy(text: string) {
    void navigator.clipboard.writeText(text);
    toast.success(t('settings.openclaw.copied'));
  }

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.openclaw.title')}</SectionTitle>
      <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.openclaw.description')}</p>

      {isLoading && <LoadingState />}

      {!isLoading && data && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <Pill size="sm" variant="subtle" tone={isConnected ? 'success' : 'muted'}>
              {isConnected ? t('settings.openclaw.connected') : t('settings.openclaw.notConnected')}
            </Pill>
            {data.lastSeenAt && (
              <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                {t('settings.openclaw.lastSeen', { date: fmtDate(data.lastSeenAt) })}
              </span>
            )}
          </div>

          <div className="space-y-3 mb-5">
            <Field label={t('settings.openclaw.endpoint_label')}>
              <div className="flex items-center gap-2">
                <Input value={data.endpoint} onChange={() => {}} size="sm" fullWidth disabled />
                <Button variant="outline" tone="muted" size="sm" onClick={() => handleCopy(data.endpoint)}>
                  {t('settings.openclaw.copy')}
                </Button>
              </div>
            </Field>

            <Field label={t('settings.openclaw.token_label')}>
              {revealedToken ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Input value={revealedToken} onChange={() => {}} size="sm" fullWidth disabled />
                    <Button variant="outline" tone="cyan" size="sm" onClick={() => handleCopy(revealedToken)}>
                      {t('settings.openclaw.copy')}
                    </Button>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--evo-danger)' }}>{t('settings.openclaw.token_shown_once')}</span>
                </div>
              ) : (
                <Button
                  variant="outline"
                  tone={data.tokenIssued ? 'muted' : 'cyan'}
                  size="sm"
                  loading={issueToken.isPending}
                  onClick={() => { void handleIssueToken(); }}
                >
                  {data.tokenIssued ? t('settings.openclaw.regenerate_btn') : t('settings.openclaw.generate_btn')}
                </Button>
              )}
            </Field>
          </div>

          <DetailSection title={t('settings.openclaw.setup_title')}>
            <ol className="text-sm space-y-2 list-decimal pl-5" style={{ color: 'var(--evo-fg-2)' }}>
              <li>{t('settings.openclaw.setup_step1')}</li>
              <li>{t('settings.openclaw.setup_step2')}</li>
              <li>{t('settings.openclaw.setup_step3')}</li>
              <li>{t('settings.openclaw.setup_step4')}</li>
            </ol>
          </DetailSection>
        </>
      )}
    </div>
  );
}

function UsersSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useUsers();
  const createUser = useCreateUser();
  const updateRole = useUpdateUserRole();
  const setActive = useSetUserActive();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const [showNew, setShowNew] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'member' as 'owner' | 'member' });
  const [formError, setFormError] = useState<string | null>(null);
  const users = data?.users ?? [];

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!newUser.email.trim() || newUser.password.length < 8) {
      setFormError(t('settings.users.validation', 'Email y contraseña (mín. 8 caracteres) requeridos.'));
      return;
    }
    createUser.mutate(
      { email: newUser.email.trim(), password: newUser.password, displayName: newUser.displayName.trim() || undefined, role: newUser.role },
      {
        onSuccess: () => { setNewUser({ email: '', password: '', displayName: '', role: 'member' }); setShowNew(false); },
        onError: (err: unknown) => setFormError(err instanceof Error ? err.message : String(err)),
      },
    );
  }

  async function handleRoleChange(userId: number, role: 'owner' | 'member') {
    updateRole.mutate({ id: userId, role }, {
      onError: (err: unknown) => alert(err instanceof Error ? err.message : String(err)),
    });
  }

  async function handleToggleActive(userId: number, active: boolean) {
    if (!active) {
      const ok = await confirmAction({ title: t('settings.users.deactivate_confirm', '¿Desactivar este usuario? Deja de poder iniciar sesión.'), tone: 'danger' });
      if (!ok) return;
    }
    setActive.mutate({ id: userId, active }, {
      onError: (err: unknown) => alert(err instanceof Error ? err.message : String(err)),
    });
  }

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.users.title', 'Usuarios')}</SectionTitle>
      <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.users.description', 'owner: acceso total, incluida esta pantalla y la suscripción. member: trabaja y ve la facturación a sus clientes, sin acceso a credenciales, integraciones ni la suscripción de la cuenta.')}
      </p>

      {isLoading && <LoadingState />}

      {!isLoading && (
        <div className="space-y-1.5 mb-4">
          {users.map(u => (
            <div
              key={u.id}
              className="flex items-center justify-between px-3 py-2 rounded"
              style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)', opacity: u.active ? 1 : 0.5 }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{u.displayName || u.email}</span>
                <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>{u.email}</span>
              </div>
              <div className="flex items-center gap-3">
                <RadioGroup
                  value={u.role}
                  options={[
                    { value: 'member', label: t('settings.users.member', 'member') },
                    { value: 'owner', label: t('settings.users.owner', 'owner') },
                  ]}
                  onChange={v => { void handleRoleChange(u.id, v as 'owner' | 'member'); }}
                />
                <Toggle
                  checked={u.active}
                  onChange={v => { void handleToggleActive(u.id, v); }}
                  size="sm"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {!showNew && (
        <Button variant="outline" tone="cyan" size="sm" onClick={() => setShowNew(true)}>
          {t('settings.users.invite_btn', 'Crear usuario')}
        </Button>
      )}

      {showNew && (
        <form onSubmit={handleCreate} className="flex flex-col gap-2 p-3 rounded" style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}>
          <Input value={newUser.email} onChange={v => setNewUser(p => ({ ...p, email: v }))} placeholder={t('settings.users.email_placeholder', 'Email')} size="sm" fullWidth />
          <Input value={newUser.password} onChange={v => setNewUser(p => ({ ...p, password: v }))} type="password" placeholder={t('settings.users.password_placeholder', 'Contraseña inicial (mín. 8 caracteres)')} size="sm" fullWidth />
          <Input value={newUser.displayName} onChange={v => setNewUser(p => ({ ...p, displayName: v }))} placeholder={t('settings.users.name_placeholder', 'Nombre (opcional)')} size="sm" fullWidth />
          <RadioGroup
            value={newUser.role}
            options={[
              { value: 'member', label: t('settings.users.member', 'member') },
              { value: 'owner', label: t('settings.users.owner', 'owner') },
            ]}
            onChange={v => setNewUser(p => ({ ...p, role: v as 'owner' | 'member' }))}
          />
          {formError && <span className="text-xs" style={{ color: 'var(--evo-danger)' }}>{formError}</span>}
          <div className="flex gap-2">
            <Button type="submit" variant="solid" tone="cyan" size="sm" loading={createUser.isPending}>
              {t('settings.users.create_btn', 'Crear')}
            </Button>
            <Button type="button" variant="ghost" tone="muted" size="sm" onClick={() => { setShowNew(false); setFormError(null); }}>
              {t('settings.categoriesAndTags.cancel_btn')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Section: Consumo (Fase 4 — Settings → Consumo) ───────────────────────────

const SERVICE_LABEL_KEYS = new Set([
  'claude', 'groq', 'elevenlabs', 'google_tts', 'composio', 'google_places', 'pagespeed',
]);

function ApiUsageSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiUsage();
  const setLimit = useSetApiUsageLimit();
  const [limitValue, setLimitValue] = useState('');
  const [limitSaved, setLimitSaved] = useState(true);

  useEffect(() => {
    if (data && limitSaved) setLimitValue(data.limitUsd != null ? String(data.limitUsd) : '');
  }, [data, limitSaved]);

  function saveLimit() {
    const trimmed = limitValue.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) return;
    setLimit.mutate(parsed, { onSuccess: () => setLimitSaved(true) });
  }

  const pctOfLimit = data?.limitUsd ? Math.min(100, (data.monthSpendUsd / data.limitUsd) * 100) : 0;
  const limitColor = pctOfLimit >= 100 ? 'var(--evo-danger)' : pctOfLimit >= 80 ? 'var(--evo-warning)' : 'var(--evo-cyan)';

  return (
    <div>
      <SectionTitle>{t('settings.apiUsage.title', 'Consumo de APIs')}</SectionTitle>
      <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>
        {t('settings.apiUsage.description', 'Consumo de APIs externas pagas este mes, por servicio.')}
      </p>

      {isLoading && <LoadingState />}

      {!isLoading && data && (
        <>
          {data.billingExempt && (
            <div className="mb-4">
              <Pill size="sm" variant="subtle" tone="muted">
                {t('settings.apiUsage.exempt', 'Cuenta exenta — nunca se corta ni se cobra')}
              </Pill>
            </div>
          )}

          <div className="space-y-3 mb-6">
            <Field label={t('settings.apiUsage.monthSpend', 'Gasto real este mes')}>
              <span className="text-sm font-mono">${data.monthSpendUsd.toFixed(2)}</span>
            </Field>
            <Field label={t('settings.apiUsage.estimatedCharge', 'Estimado a cobrar (doble del real)')}>
              <span className="text-sm font-mono">${data.estimatedChargeUsd.toFixed(2)}</span>
            </Field>
          </div>

          <div className="overflow-auto rounded mb-6" style={{ border: '1px solid var(--evo-stroke)' }}>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
                  <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.apiUsage.headers.service', 'Servicio')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.apiUsage.headers.quantity', 'Volumen')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--evo-fg-3)' }}>
                    {t('settings.apiUsage.headers.cost', 'Costo')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.byService.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.apiUsage.noUsage', 'Sin consumo este mes')}</td></tr>
                )}
                {data.byService.map(row => (
                  <tr key={row.service} style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
                    <td className="px-3 py-2 font-medium">
                      {SERVICE_LABEL_KEYS.has(row.service) ? t(`settings.apiUsage.services.${row.service}`, row.service) : row.service}
                    </td>
                    <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--evo-fg-3)' }}>{row.quantity.toLocaleString('es')}</td>
                    <td className="px-3 py-2 text-right font-mono">${row.costUsd.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Field label={t('settings.apiUsage.limitLabel', 'Límite mensual')} hint={t('settings.apiUsage.limitHint', 'Vacío = sin límite. Al superarlo se corta el consumo de APIs pagas sin afectar el resto de EvoHub.')}>
            <div className="flex items-center gap-2">
              <span className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>$</span>
              <div style={{ width: '96px', flexShrink: 0 }}>
                <Input
                  type="number"
                  min={0}
                  step={10}
                  value={limitValue}
                  onChange={v => { setLimitValue(v); setLimitSaved(false); }}
                  placeholder={t('settings.apiUsage.limitPlaceholder', 'sin límite')}
                  size="sm"
                  fullWidth
                />
              </div>
              <Button size="sm" tone="cyan" variant={limitSaved ? 'outline' : 'solid'} loading={setLimit.isPending} onClick={saveLimit}>
                {setLimit.isPending ? t('settings.savingBtn') : limitSaved ? t('settings.savedBtn') : t('settings.saveBtn')}
              </Button>
            </div>
          </Field>

          {data.limitUsd != null && (
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full" style={{ background: 'var(--evo-bg-2)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pctOfLimit}%`, background: limitColor }} />
                </div>
                <span className="text-sm font-mono w-28 text-right" style={{ color: limitColor }}>
                  ${data.monthSpendUsd.toFixed(2)} / ${data.limitUsd.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Section: Suscripción (Fase 3 — Settings → Suscripción) ───────────────────

const SUBSCRIPTION_STATUS_TONE: Record<SubscriptionSummary['status'], 'success' | 'warn' | 'danger'> = {
  active: 'success', grace_period: 'warn', suspended: 'danger',
};

function fmtMoney(amountCents: number | null, currency: string | null): string {
  if (amountCents == null || !currency) return '—';
  return `${(amountCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
}

function SubscriptionSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useSubscription();
  const setPrice = useSetSubscriptionPrice();
  const checkout = useCreateSubscriptionCheckout();
  const portal = useCreateBillingPortal();
  const toast = useToast();
  const [priceValue, setPriceValue] = useState('');
  const [priceSaved, setPriceSaved] = useState(true);

  useEffect(() => {
    if (data && priceSaved) setPriceValue(data.priceId ?? '');
  }, [data, priceSaved]);

  function savePrice() {
    if (!priceValue.trim()) return;
    setPrice.mutate(priceValue.trim(), { onSuccess: () => setPriceSaved(true) });
  }

  function goToCheckout() {
    checkout.mutate(undefined, {
      onSuccess: r => { window.location.href = r.url; },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  function goToPortal() {
    portal.mutate(undefined, {
      onSuccess: r => { window.location.href = r.url; },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  const graceDaysLeft = data ? daysUntil(data.gracePeriodEndsAt) : null;

  return (
    <div>
      <SectionTitle>{t('settings.subscription.title', 'Suscripción')}</SectionTitle>

      {isLoading && <LoadingState />}

      {!isLoading && data && (
        <>
          {data.billingExempt ? (
            <Pill size="sm" variant="subtle" tone="muted">
              {t('settings.subscription.exempt', 'Cuenta exenta — no paga suscripción')}
            </Pill>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                <Pill size="sm" variant="subtle" tone={SUBSCRIPTION_STATUS_TONE[data.status]}>
                  {t(`settings.subscription.status.${data.status}`, data.status)}
                </Pill>
                {data.status === 'grace_period' && graceDaysLeft != null && (
                  <span className="text-xs" style={{ color: 'var(--evo-warning)' }}>
                    {t('settings.subscription.graceDaysLeft', `Pago pendiente — ${graceDaysLeft} día(s) antes de suspender`, { count: graceDaysLeft })}
                  </span>
                )}
                {data.status === 'suspended' && (
                  <span className="text-xs" style={{ color: 'var(--evo-danger)' }}>
                    {t('settings.subscription.suspendedNotice', 'Cuenta en solo lectura — regularizá el pago para volver a operar')}
                  </span>
                )}
              </div>

              <div className="space-y-3 mb-5">
                <Field label={t('settings.subscription.priceLabel', 'Price ID de Stripe')} hint={t('settings.subscription.priceHint', 'El precio mensual que paga esta cuenta.')}>
                  <div className="flex items-center gap-2">
                    <Input
                      value={priceValue}
                      onChange={v => { setPriceValue(v); setPriceSaved(false); }}
                      placeholder="price_..."
                      size="sm"
                      fullWidth
                    />
                    <Button size="sm" tone="cyan" variant={priceSaved ? 'outline' : 'solid'} loading={setPrice.isPending} onClick={savePrice}>
                      {setPrice.isPending ? t('settings.savingBtn') : priceSaved ? t('settings.savedBtn') : t('settings.saveBtn')}
                    </Button>
                  </div>
                </Field>

                {!data.hasCustomer && (
                  <Button variant="solid" tone="cyan" size="sm" loading={checkout.isPending} disabled={!data.priceId} onClick={goToCheckout}>
                    {t('settings.subscription.subscribeBtn', 'Suscribirme')}
                  </Button>
                )}

                {data.hasCustomer && (
                  <>
                    <Field label={t('settings.subscription.nextCharge', 'Próximo cobro')}>
                      <span className="text-sm">
                        {data.nextChargeAt ? fmtDate(data.nextChargeAt) : '—'} — {fmtMoney(data.nextChargeAmount, data.nextChargeCurrency)}
                      </span>
                    </Field>
                    {data.paymentMethod && (
                      <Field label={t('settings.subscription.paymentMethod', 'Método de pago')}>
                        <span className="text-sm capitalize">{data.paymentMethod.brand} •••• {data.paymentMethod.last4}</span>
                      </Field>
                    )}
                    <Button variant="outline" tone="muted" size="sm" loading={portal.isPending} onClick={goToPortal}>
                      {t('settings.subscription.managePaymentBtn', 'Gestionar método de pago')}
                    </Button>
                  </>
                )}
              </div>

              {data.invoices.length > 0 && (
                <div className="overflow-auto rounded" style={{ border: '1px solid var(--evo-stroke)' }}>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--evo-stroke)', background: 'var(--evo-bg-2)' }}>
                        <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.subscription.invoiceHeaders.date', 'Fecha')}</th>
                        <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.subscription.invoiceHeaders.amount', 'Monto')}</th>
                        <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.subscription.invoiceHeaders.status', 'Estado')}</th>
                        <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--evo-fg-3)' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.invoices.map(inv => (
                        <tr key={inv.id} style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
                          <td className="px-3 py-2">{inv.date ? fmtDate(inv.date) : '—'}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtMoney(inv.amountPaid, inv.currency)}</td>
                          <td className="px-3 py-2" style={{ color: 'var(--evo-fg-3)' }}>{inv.status ?? '—'}</td>
                          <td className="px-3 py-2">
                            {inv.hostedInvoiceUrl && (
                              <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--evo-cyan)' }}>
                                {t('settings.subscription.viewInvoice', 'Ver')}
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function EvoBrainSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useToolApprovals();
  const revoke = useRevokeToolApproval();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const approvals = data?.approvals ?? [];

  async function handleRevoke(toolName: string, label: string) {
    const ok = await confirmAction({ title: t('settings.evobrain.revoke_confirm', { label }), tone: 'danger' });
    if (!ok) return;
    revoke.mutate(toolName);
  }

  return (
    <div>
      {confirmDialog}
      <SectionTitle>{t('settings.evobrain.title')}</SectionTitle>
      <p className="text-sm mb-4" style={{ color: 'var(--evo-fg-3)' }}>{t('settings.evobrain.description')}</p>

      {isLoading && <LoadingState />}

      {!isLoading && approvals.length === 0 && (
        <EmptyState title={t('settings.evobrain.empty_title')} description={t('settings.evobrain.empty_description')} size="sm" />
      )}

      {!isLoading && approvals.length > 0 && (
        <div className="space-y-1.5">
          {approvals.map(a => (
            <div
              key={a.toolName}
              className="flex items-center justify-between px-3 py-2 rounded"
              style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{a.label}</span>
                <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                  {t('settings.evobrain.granted_at', { date: fmtDate(a.grantedAt) })}
                </span>
              </div>
              <IconButton
                icon={<Icon name="close" size={14} />}
                ariaLabel={t('settings.evobrain.revoke')}
                tone="muted"
                variant="outline"
                size="md"
                onClick={() => { void handleRevoke(a.toolName, a.label); }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

// Fase 2 (2026-08-04): ownerOnly acá es solo UI — oculta la tab para no
// mostrar un botón que va a 403. El chequeo real es requireOwner() en el
// backend (routes/users.ts, routes/settings.ts, routes/email.ts) — esto
// nunca es la única barrera.
const TABS = [
  { id: 'general' },
  { id: 'api', ownerOnly: true },
  { id: 'imap', ownerOnly: true },
  { id: 'rules' },
  { id: 'followup' },
  { id: 'lead-engine' },
  { id: 'billing' },
  { id: 'cleanup' },
  { id: 'audit' },
  { id: 'categories' },
  { id: 'evobrain' },
  { id: 'openclaw', ownerOnly: true },
  { id: 'users', ownerOnly: true },
  { id: 'api-usage', ownerOnly: true },
  { id: 'subscription', ownerOnly: true },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function SettingsPage() {
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const { role } = useSession();
  useEffect(() => {
    setOverride({ title: t('settings.pageTitle'), heroIcon: 'settings-large', heroIconColor: 'var(--evo-fg-3)' });
    return () => setOverride({});
  }, [setOverride, t]);
  const [tab, setTab] = useState<TabId>('general');
  // Fase 2: solo UI — el 403 real vive en el backend (requireOwner()). Un
  // member nunca ve el botón de una tab a la que igual no puede entrar.
  const visibleTabs = TABS.filter(tabItem => !('ownerOnly' in tabItem) || role === 'owner');

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      {/* Tab bar */}
      <div
        className="shrink-0 px-6"
        style={{
          height: 'var(--evo-subnav-h)', borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex', alignItems: 'flex-end',
          overflowX: 'auto', overflowY: 'hidden',
        }}
      >
        <Tabs<TabId>
          tabs={visibleTabs.map(tabItem => ({
            value: tabItem.id,
            label: t(`settings.tabs.${tabItem.id === 'api' ? 'ia' : tabItem.id}`),
          }))}
          value={tab}
          onChange={v => setTab(v)}
          size="sm"
          noBorder
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl px-6 py-6">
          {tab === 'general' && <GeneralSection />}
          {tab === 'api' && <><ApiSection /><ImageGenerationSection /></>}
          {tab === 'imap' && <ImapSection />}
          {tab === 'rules' && <RulesSection />}
          {tab === 'followup' && <FollowupRulesSection />}
          {tab === 'lead-engine' && <LeadEngineSection />}
          {tab === 'billing' && <BillingSection />}
          {tab === 'cleanup' && <CleanupSection />}
          {tab === 'audit' && <AuditSection />}
          {tab === 'categories' && <CategoriesTagsSection />}
          {tab === 'evobrain' && <EvoBrainSection />}
          {tab === 'openclaw' && <OpenClawSection />}
          {tab === 'users' && <UsersSection />}
          {tab === 'api-usage' && <ApiUsageSection />}
          {tab === 'subscription' && <SubscriptionSection />}
        </div>
      </div>
    </div>
  );
}
